export type GeoJsonDatasetCredit = {
  name: string;
  url?: string;
  description?: string; // Role: "Compiled for helloquiz.app", "Original geometry source", etc.
};

export type GeoJsonDataset = {
  id: string;
  title: string;
  description: string;
  path: string;
  geography: string;
  topics: string[];
  credits: GeoJsonDatasetCredit[]; // Array for multi-level attribution
  featureCount: number;
  propertyKeys: string[];
};

export const geojsonDatasets: GeoJsonDataset[] = [
  {
    id: "phones-ar-3",
    title: "Argentina · Phone Area Codes (2-4 digits)",
    description:
      "Area codes for Argentina's provinces and major cities. Includes 2-4 digit codes covering all 23 provinces plus Buenos Aires (Capital Federal).",
    path: "/geojson/phones/AR_3.json",
    geography: "Argentina",
    topics: ["Phone Area Codes", "Argentina"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 81,
    propertyKeys: ["AreaCode", "LargestCity", "Province"],
  },
  {
    id: "phones-be-2",
    title: "Belgium · Phone Area Codes (2 digits)",
    description:
      "Two-digit area codes covering Belgium's provinces and regions. Includes Flanders, Wallonia, and Brussels.",
    path: "/geojson/phones/BE_2.json",
    geography: "Belgium",
    topics: ["Phone Area Codes", "Belgium"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 40,
    propertyKeys: ["AreaCode", "Country"],
  },
  {
    id: "phones-br-2",
    title: "Brazil · Phone Area Codes (2 digits)",
    description:
      "Two-digit area codes for Brazil's states and major cities. Covers regions from the Amazon to the southern states.",
    path: "/geojson/phones/BR_2.json",
    geography: "Brazil",
    topics: ["Phone Area Codes", "Brazil"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 67,
    propertyKeys: ["AreaCode", "Country", "LargestCity", "State"],
  },
  {
    id: "phones-ca-3",
    title: "Canada · Phone Area Codes (3 digits)",
    description:
      "Three-digit area codes for Canadian provinces and territories. Includes both older codes and recent additions from area code splits.",
    path: "/geojson/phones/CA_3.json",
    geography: "Canada",
    topics: ["Phone Area Codes", "Canada"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 19,
    propertyKeys: ["AreaCode", "Country"],
  },
  {
    id: "phones-cl-2",
    title: "Chile · Phone Area Codes (1-2 digits)",
    description:
      "Area codes for Chile's regions and major cities. Covers all 16 regions from Arica and Parinacota in the north to Magallanes in the south, including Santiago Metropolitan Region.",
    path: "/geojson/phones/CL_2.json",
    geography: "Chile",
    topics: ["Phone Area Codes", "Chile"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 24,
    propertyKeys: ["AreaCode", "LargestCity", "Region"],
  },
  {
    id: "phones-fr-4",
    title: "France · Phone Area Codes (4 digits)",
    description:
      "Four-digit area codes for France's départements and regions. Codes follow the '0X XX' format used since the 1990s.",
    path: "/geojson/phones/FR_3.json",
    geography: "France",
    topics: ["Phone Area Codes", "France"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 71,
    propertyKeys: ["AreaCode", "Country", "LargestCity"],
  },
  {
    id: "phones-world",
    title: "World · Phone Dialing Codes",
    description:
      "Combined area codes from multiple countries. Includes data from Belgium, Brazil, Canada, Spain, France, Portugal, and the United States.",
    path: "/geojson/phones/world.json",
    geography: "Global",
    topics: ["Phone Area Codes", "Global"],
    credits: [{
      name: "emily",
      description: "Merged dataset from emily's regional maps",
    }],
    featureCount: 254,
    propertyKeys: ["AreaCode", "Country", "source"],
  },
  {
    id: "regions-id-2",
    title: "Indonesia · Regencies and Cities (Level 2)",
    description:
      "Indonesia's kabupaten (regencies) and kota (cities) mapped by province. Contains 514 administrative divisions across 38 provinces, including Google Street View availability data and island groupings.",
    path: "/geojson/regions/ID_2.json",
    geography: "Indonesia",
    topics: ["Administrative Regions", "Indonesia", "Street View"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 510,
    propertyKeys: ["Island", "Name", "OnStreetView", "Province"],
  },
  {
    id: "phones-it-2",
    title: "Italy · Phone Area Codes (2-3 digits)",
    description:
      "Area codes for Italy's regions and major cities. Includes 2-3 digit codes covering all 20 regions from Lombardy to Sicily.",
    path: "/geojson/phones/IT_2.json",
    geography: "Italy",
    topics: ["Phone Area Codes", "Italy"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 71,
    propertyKeys: ["AreaCode", "LargestCity", "Region"],
  },
  {
    id: "phones-jp-2",
    title: "Japan · Phone Area Codes (1-3 digits)",
    description:
      "Area codes for Japan's prefectures and major cities. Includes codes ranging from single digits for major metropolitan areas to 3-digit codes for smaller regions, covering all 47 prefectures.",
    path: "/geojson/phones/JP_2.json",
    geography: "Japan",
    topics: ["Phone Area Codes", "Japan"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 59,
    propertyKeys: ["AreaCode", "LargestCity", "Prefecture"],
  },
  {
    id: "phones-pt-2",
    title: "Portugal · Phone Area Codes (2 digits)",
    description:
      "Two-digit area codes for Portugal's mainland districts and island regions. Codes follow the 2x pattern.",
    path: "/geojson/phones/PT_2.json",
    geography: "Portugal",
    topics: ["Phone Area Codes", "Portugal"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 9,
    propertyKeys: ["AreaCode", "Country"],
  },
  {
    id: "phones-pl-2",
    title: "Poland · Phone Area Codes (2 digits)",
    description:
      "Navigate Poland's two-digit area codes from Warsaw to Wrocław! Master the geographic spread of cities across this Central European nation, from the Baltic coast to the Tatra mountains.",
    path: "/geojson/phones/PL_2.json",
    geography: "Poland",
    topics: ["Phone Area Codes", "Poland"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 49,
    propertyKeys: ["AreaCode", "City"],
  },
  {
    id: "phones-ru-3",
    title: "Russia · Phone Area Codes (3 digits)",
    description:
      "Three-digit area codes for Russia's federal subjects and regions. Covers the world's largest country by area with comprehensive regional code coverage.",
    path: "/geojson/phones/RU_3.json",
    geography: "Russia",
    topics: ["Phone Area Codes", "Russia"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 81,
    propertyKeys: ["AdministrativeCentre", "AreaCode", "FederalDistrict", "FederalSubject"],
  },
  {
    id: "phones-se-2",
    title: "Sweden · Phone Area Codes (2-4 digits)",
    description:
      "Area codes for Sweden's 290 municipalities. Includes 2-4 digit codes covering major cities like Stockholm (08), Gothenburg (031), and Malmö (040), as well as smaller municipalities across the country. Enriched from Wikipedia telephone data.",
    path: "/geojson/phones/SWE_PHONE_AREAS.json",
    geography: "Sweden",
    topics: ["Phone Area Codes", "Sweden", "Nordic Countries"],
    credits: [{
      name: "Wikipedia contributors",
      url: "https://en.wikipedia.org/wiki/Telephone_numbers_in_Sweden",
      description: "Enriched from Wikipedia telephone data using point-in-polygon matching",
    }],
    featureCount: 290,
    propertyKeys: ["Area", "Municipality"],
  },
  {
    id: "phones-se-3",
    title: "Sweden · Phone Area Codes (3-digit merged regions)",
    description:
      "Merged regional phone areas for Sweden at 3-digit precision. Contains 65 area codes created by merging municipalities that share the same first 3 digits. For example, municipalities with codes 0243 and 0241 are merged into region 024. Each region lists the municipalities it encompasses.",
    path: "/geojson/phones/SWE_PHONE_AREAS_3.json",
    geography: "Sweden",
    topics: ["Phone Area Codes", "Sweden", "Nordic Countries"],
    credits: [{
      name: "Wikipedia contributors",
      url: "https://en.wikipedia.org/wiki/Telephone_numbers_in_Sweden",
      description: "Derived from SWE_PHONE_AREAS.json by merging regions with shared 3-digit prefixes using Turf.js geometry unions",
    }],
    featureCount: 65,
    propertyKeys: ["Area", "Municipality"],
  },
  {
    id: "phones-za-2",
    title: "South Africa · Phone Area Codes (2 digits)",
    description:
      "Two-digit area codes for South Africa's provinces and major cities. Covers all 9 provinces including Gauteng, Western Cape, and KwaZulu-Natal.",
    path: "/geojson/phones/ZA_2.json",
    geography: "South Africa",
    topics: ["Phone Area Codes", "South Africa"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 36,
    propertyKeys: ["AreaCode", "LargestCity", "Province"],
  },
  {
    id: "phones-es-3",
    title: "Spain · Phone Area Codes (3 digits)",
    description:
      "Three-digit area codes for Spain's provinces and autonomous communities. Codes follow the 9x or 9xx pattern.",
    path: "/geojson/phones/ES_2.json",
    geography: "Spain",
    topics: ["Phone Area Codes", "Spain"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 48,
    propertyKeys: ["AreaCode", "Country", "HighwayPrefix"],
  },
  {
    id: "phones-ua-2",
    title: "Ukraine · Phone Area Codes (2 digits)",
    description:
      "Two-digit area codes for Ukraine's oblasts and major cities. Covers all 24 oblasts plus Crimea and the capital Kyiv.",
    path: "/geojson/phones/UA_2.json",
    geography: "Ukraine",
    topics: ["Phone Area Codes", "Ukraine"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 27,
    propertyKeys: ["AreaCode", "LargestCity", "Oblast"],
  },
  {
    id: "phones-us-3",
    title: "USA · Phone Area Codes (3 digits)",
    description:
      "Three-digit area codes (NPAs) for US states and territories. Contains over 300 codes including overlays and recent additions.",
    path: "/geojson/phones/US_3.json",
    geography: "USA",
    topics: ["Phone Area Codes", "USA"],
    credits: [{
      name: "emily",
      description: "Compiled by emily for helloquiz.app",
    }],
    featureCount: 244,
    propertyKeys: ["AreaCode", "State"],
  },
  {
    id: "regions-world",
    title: "World · Countries and Territories",
    description:
      "Comprehensive world dataset covering 258 countries and territories. Includes 194 independent countries, 52 territories/dependencies, and 12 disputed areas. Enriched with capital cities, phone codes, internet domains, currencies, languages, and sovereignty information.",
    path: "/geojson/regions/WORLD.json",
    geography: "Global",
    topics: ["Countries", "World Geography", "Capitals", "Sovereignty"],
    credits: [{
      name: "Natural Earth",
      url: "https://www.naturalearthdata.com/",
      description: "Enriched with quiz-friendly properties",
    }],
    featureCount: 258,
    propertyKeys: [
      "name",
      "Capital",
      "Continent",
      "Currency",
      "Domain",
      "Independent",
      "Language",
      "PhoneCode",
      "Region",
      "SovereignState",
    ],
  },
  {
    id: "dnk-adm1",
    title: "Denmark · Regions (1st level)",
    description:
      "Denmark's 5 administrative regions (regioner) established in 2007. Includes Region Hovedstaden, Region Sjælland, Region Syddanmark, Region Midtjylland, and Region Nordjylland.",
    path: "/geojson/regions/adm/DNK_ADM1.json",
    geography: "Denmark",
    topics: ["Administrative Regions", "Denmark", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 5,
    propertyKeys: ["Name"],
  },
  {
    id: "dnk-adm2",
    title: "Denmark · Municipalities (2nd level)",
    description:
      "Denmark's 98 municipalities (kommuner), the primary administrative subdivisions. Covers all local government areas across the country.",
    path: "/geojson/regions/adm/DNK_ADM2.json",
    geography: "Denmark",
    topics: ["Municipalities", "Denmark", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 98,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "lux-adm1",
    title: "Luxembourg · Cantons (1st level)",
    description:
      "Luxembourg's 12 cantons, the first-level administrative divisions. Includes Luxembourg, Capellen, Diekirch, Echternach, and others.",
    path: "/geojson/regions/adm/LUX_ADM1.json",
    geography: "Luxembourg",
    topics: ["Administrative Regions", "Luxembourg", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 12,
    propertyKeys: ["Name"],
  },
  {
    id: "lux-adm2",
    title: "Luxembourg · Communes (2nd level)",
    description:
      "Luxembourg's 105 communes, the primary municipal subdivisions. These are the local government areas within the cantons.",
    path: "/geojson/regions/adm/LUX_ADM2.json",
    geography: "Luxembourg",
    topics: ["Municipalities", "Luxembourg", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 105,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "lux-adm3",
    title: "Luxembourg · Quarters (3rd level)",
    description:
      "Luxembourg's 102 quarters and sections, the third-level administrative subdivisions within communes.",
    path: "/geojson/regions/adm/LUX_ADM3.json",
    geography: "Luxembourg",
    topics: ["Administrative Regions", "Luxembourg", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 102,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "mlt-adm1",
    title: "Malta · Localities (1st level)",
    description:
      "Malta's 68 localities, including cities, towns, and villages across Malta, Gozo, and Comino islands. Includes Valletta, Mdina, Victoria, and other historic settlements.",
    path: "/geojson/regions/adm/MLT_ADM1.json",
    geography: "Malta",
    topics: ["Administrative Regions", "Malta", "Mediterranean"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 68,
    propertyKeys: ["Name"],
  },
  {
    id: "sgp-adm1",
    title: "Singapore · Regions (1st level)",
    description:
      "Singapore's 5 planning regions: Central, East, North, North-East, and West. These are the highest-level administrative divisions in the city-state.",
    path: "/geojson/regions/adm/SGP_ADM1.json",
    geography: "Singapore",
    topics: ["Administrative Regions", "Singapore", "Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 5,
    propertyKeys: ["Name"],
  },
  {
    id: "sgp-adm2",
    title: "Singapore · Planning Areas (2nd level)",
    description:
      "Singapore's 55 planning areas, the urban planning subdivisions within the 5 regions. Includes areas like Jurong, Tampines, Woodlands, and the Central Area.",
    path: "/geojson/regions/adm/SGP_ADM2.json",
    geography: "Singapore",
    topics: ["Administrative Regions", "Singapore", "Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 55,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "isl-adm1",
    title: "Iceland · Regions (1st level)",
    description:
      "Iceland's 8 regions (landshluti): Capital Region, Southern Peninsula, West, Westfjords, Northwest, Northeast, East, and South. These are the first-level administrative divisions.",
    path: "/geojson/regions/adm/ISL_ADM1.json",
    geography: "Iceland",
    topics: ["Administrative Regions", "Iceland", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 8,
    propertyKeys: ["Name"],
  },
  {
    id: "isl-adm2",
    title: "Iceland · Municipalities (2nd level)",
    description:
      "Iceland's 74 municipalities (sveitarfélög), the primary local government areas. Includes Reykjavík, Akureyri, Kópavogur, and other towns and rural areas.",
    path: "/geojson/regions/adm/ISL_ADM2.json",
    geography: "Iceland",
    topics: ["Municipalities", "Iceland", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 74,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "nor-adm1",
    title: "Norway · Counties (1st level)",
    description:
      "Norway's 11 counties (fylker) as of 2020 after regional reforms. Includes Oslo, Viken, Innlandet, Vestfold og Telemark, Agder, Rogaland, Vestland, Møre og Romsdal, Trøndelag, Nordland, and Troms og Finnmark.",
    path: "/geojson/regions/adm/NOR_ADM1.json",
    geography: "Norway",
    topics: ["Administrative Regions", "Norway", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 11,
    propertyKeys: ["Name"],
  },
  {
    id: "nor-adm2",
    title: "Norway · Municipalities (2nd level)",
    description:
      "Norway's 431 municipalities (kommuner), the primary local government areas across the country. Includes major cities like Oslo, Bergen, Trondheim, and Stavanger, plus numerous smaller municipalities.",
    path: "/geojson/regions/adm/NOR_ADM2.json",
    geography: "Norway",
    topics: ["Municipalities", "Norway", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 431,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "alb-adm1",
    title: "Albania · Counties (1st level)",
    description: "Albania's 12 counties (qarqe) stretch from the Adriatic coast to the mountainous interior. These regions were established after the fall of communism in 1991, replacing the old Soviet-style district system. From the bustling capital of Tirana to the historic cities of Shkodër and Gjirokastër, each county has its own distinct character and landscape.",
    path: "/geojson/regions/adm/ALB_ADM1.json",
    geography: "Albania",
    topics: ["Administrative Regions", "Albania", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 12,
    propertyKeys: ["Name"],
  },
  {
    id: "alb-adm2",
    title: "Albania · Districts (2nd level)",
    description: "The 37 districts (rrethe) of Albania form an intermediate administrative layer between counties and municipalities. These districts reflect Albania's diverse geography, from coastal plains to rugged mountain ranges. Many district boundaries follow natural features like rivers and mountain ridges.",
    path: "/geojson/regions/adm/ALB_ADM2.json",
    geography: "Albania",
    topics: ["Administrative Regions", "Albania", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 37,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "alb-adm3",
    title: "Albania · Municipalities (3rd level)",
    description: "Albania's 373 municipalities and communes represent the most local level of government, serving communities from tiny mountain villages to coastal towns. These units were reformed in 2015 to consolidate what were previously over 2,900 smaller administrative units. Each municipality manages local services, from education to infrastructure in their communities.",
    path: "/geojson/regions/adm/ALB_ADM3.json",
    geography: "Albania",
    topics: ["Administrative Regions", "Albania", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 373,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "aut-adm1",
    title: "Austria · States (1st level)",
    description: "Austria's 9 federal states (Bundesländer) range from the Alpine majesty of Tyrol to the imperial grandeur of Vienna. Each state has its own constitution and significant autonomy, reflecting Austria's federal character. From the wine country of Burgenland to the lakeside beauty of Carinthia, these states showcase incredible geographic and cultural diversity.",
    path: "/geojson/regions/adm/AUT_ADM1.json",
    geography: "Austria",
    topics: ["Administrative Regions", "Austria", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 9,
    propertyKeys: ["Name"],
  },
  {
    id: "aut-adm2",
    title: "Austria · Districts (2nd level)",
    description: "Austria's 94 districts (Bezirke) serve as the primary administrative subdivisions within the federal states. These districts range from densely populated urban areas to sparsely populated Alpine regions. The district system balances efficient administration with local representation, making it a cornerstone of Austrian governance.",
    path: "/geojson/regions/adm/AUT_ADM2.json",
    geography: "Austria",
    topics: ["Administrative Regions", "Austria", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 94,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "bel-adm1",
    title: "Belgium · Regions (1st level)",
    description: "Belgium's 3 regions - Flanders, Wallonia, and Brussels-Capital - represent the country's complex federal structure and linguistic divisions. Dutch-speaking Flanders in the north, French-speaking Wallonia in the south, and bilingual Brussels form distinct political and cultural entities. This regional autonomy reflects Belgium's unique approach to managing its linguistic and cultural diversity.",
    path: "/geojson/regions/adm/BEL_ADM1.json",
    geography: "Belgium",
    topics: ["Administrative Regions", "Belgium", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 3,
    propertyKeys: ["Name"],
  },
  {
    id: "bel-adm2",
    title: "Belgium · Provinces (2nd level)",
    description: "Belgium's 10 provinces plus Brussels-Capital Region form the intermediate administrative level between regions and municipalities. These provinces trace their roots to the medieval territories of the Low Countries. From West Flanders on the North Sea coast to Luxembourg province (not to be confused with the country) in the Ardennes, each has its own distinct identity.",
    path: "/geojson/regions/adm/BEL_ADM2.json",
    geography: "Belgium",
    topics: ["Administrative Regions", "Belgium", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 11,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "bel-adm3",
    title: "Belgium · Arrondissements (3rd level)",
    description: "The 43 administrative arrondissements of Belgium serve as electoral and judicial districts within the provinces. These subdivisions often reflect historical boundaries and economic regions. They play a crucial role in organizing courts, elections, and various public services across the country.",
    path: "/geojson/regions/adm/BEL_ADM3.json",
    geography: "Belgium",
    topics: ["Administrative Regions", "Belgium", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 43,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "bgr-adm1",
    title: "Bulgaria · Provinces (1st level)",
    description: "Bulgaria's 28 provinces (oblasti) span from the Black Sea coast to the mountainous borders with Serbia and North Macedonia. Sofia City stands alone as its own province, while the remaining 27 provinces are named after their administrative centers. These provinces range from the wine-producing Danube plains to the ski resorts of the Rhodope Mountains.",
    path: "/geojson/regions/adm/BGR_ADM1.json",
    geography: "Bulgaria",
    topics: ["Administrative Regions", "Bulgaria", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 28,
    propertyKeys: ["Name"],
  },
  {
    id: "bgr-adm2",
    title: "Bulgaria · Municipalities (2nd level)",
    description: "Bulgaria's 265 municipalities (obshtini) form the backbone of local government, managing everything from schools to waste collection. These units were reformed in the 1990s following the end of communist rule. The municipalities vary dramatically in size, from tiny mountain communities of a few hundred people to the sprawling Sofia municipality with over a million residents.",
    path: "/geojson/regions/adm/BGR_ADM2.json",
    geography: "Bulgaria",
    topics: ["Administrative Regions", "Bulgaria", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 265,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "can-adm2",
    title: "Canada · Census Divisions (2nd level)",
    description: "Canada's 76 census divisions represent groupings of municipalities used for statistical and planning purposes. From Ontario's regional municipalities to Quebec's regional county municipalities, these divisions reflect Canada's vast geographic and administrative diversity. They're essential for understanding everything from population trends to economic development across the world's second-largest country.",
    path: "/geojson/regions/adm/CAN_ADM2.json",
    geography: "Canada",
    topics: ["Administrative Regions", "Canada", "North America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 76,
    propertyKeys: ["Name"],
  },
  {
    id: "che-adm1",
    title: "Switzerland · Cantons (1st level)",
    description: "Switzerland's 26 cantons are the founding members of the Swiss Confederation, each with remarkable autonomy and their own constitutions. These range from tiny Basel-City to sprawling Graubünden in the Alps. The cantons reflect Switzerland's multilingual heritage, with German, French, Italian, and Romansh-speaking regions living side by side in this federal democracy.",
    path: "/geojson/regions/adm/CHE_ADM1.json",
    geography: "Switzerland",
    topics: ["Administrative Regions", "Switzerland", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 26,
    propertyKeys: ["Name"],
  },
  {
    id: "che-adm2",
    title: "Switzerland · Districts (2nd level)",
    description: "Switzerland's 169 districts (Bezirke) serve as intermediate administrative units within the cantons. These districts vary greatly in importance depending on the canton - some use them actively for governance, while others maintain them primarily for statistical purposes. The district boundaries often follow natural geographical features like mountain valleys and lakeshores.",
    path: "/geojson/regions/adm/CHE_ADM2.json",
    geography: "Switzerland",
    topics: ["Administrative Regions", "Switzerland", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 169,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "cri-adm1",
    title: "Costa Rica · Provinces (1st level)",
    description: "Costa Rica's 7 provinces stretch from the Caribbean to the Pacific, encompassing rainforests, cloud forests, and volcanic mountains. These provinces were established shortly after independence in the 1820s. From the bustling capital in San José province to the pristine beaches of Guanacaste, each province offers distinct ecosystems and cultural traditions.",
    path: "/geojson/regions/adm/CRI_ADM1.json",
    geography: "Costa Rica",
    topics: ["Administrative Regions", "Costa Rica", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 7,
    propertyKeys: ["Name"],
  },
  {
    id: "cri-adm2",
    title: "Costa Rica · Cantons (2nd level)",
    description: "Costa Rica's 83 cantons (cantones) serve as the primary units of local government, each governed by an elected municipal council. These cantons range from densely populated urban areas like San José to remote rural regions bordering Nicaragua and Panama. The canton system reflects Costa Rica's commitment to local democracy and environmental stewardship.",
    path: "/geojson/regions/adm/CRI_ADM2.json",
    geography: "Costa Rica",
    topics: ["Administrative Regions", "Costa Rica", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 83,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "cri-adm3",
    title: "Costa Rica · Districts (3rd level)",
    description: "Costa Rica's 472 districts (distritos) represent the most local level of administration, often encompassing just a single town or small cluster of communities. These districts help deliver services from education to healthcare across Costa Rica's diverse terrain. Many districts are named after their central town or prominent geographical features like rivers and mountains.",
    path: "/geojson/regions/adm/CRI_ADM3.json",
    geography: "Costa Rica",
    topics: ["Administrative Regions", "Costa Rica", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 472,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "cze-adm1",
    title: "Czech Republic · Regions (1st level)",
    description: "The Czech Republic's 14 regions (kraje) were created in 2000 to replace the old system of larger districts. Prague stands alone as both a city and a region, while the other 13 regions include historic areas like Bohemia and Moravia. These regions manage everything from healthcare to regional development, balancing modern governance with deep historical roots.",
    path: "/geojson/regions/adm/CZE_ADM1.json",
    geography: "Czech Republic",
    topics: ["Administrative Regions", "Czech Republic", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 14,
    propertyKeys: ["Name"],
  },
  {
    id: "cze-adm2",
    title: "Czech Republic · Districts (2nd level)",
    description: "The Czech Republic's 77 districts (okresy) form an intermediate administrative layer, though their role has diminished since regional reforms in the early 2000s. These districts often reflect historical territories and natural boundaries. They remain important for organizing courts, police services, and land registries across the country.",
    path: "/geojson/regions/adm/CZE_ADM2.json",
    geography: "Czech Republic",
    topics: ["Administrative Regions", "Czech Republic", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 77,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "deu-adm1",
    title: "Germany · States (1st level)",
    description: "Germany's 16 federal states (Bundesländer) include historic kingdoms like Bavaria and Saxony alongside city-states like Hamburg and Bremen. Each state has significant autonomy over education, policing, and cultural affairs in Germany's federal system. From the industrial heartland of North Rhine-Westphalia to the maritime culture of Mecklenburg-Vorpommern, these states showcase Germany's regional diversity.",
    path: "/geojson/regions/adm/DEU_ADM1.json",
    geography: "Germany",
    topics: ["Administrative Regions", "Germany", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 16,
    propertyKeys: ["Name"],
  },
  {
    id: "deu-adm2",
    title: "Germany · Administrative Regions (2nd level)",
    description: "Germany's 38 government regions (Regierungsbezirke) serve as intermediate administrative units in larger states like Bavaria and North Rhine-Westphalia. Not all states use this level of administration - smaller states like Hamburg and Berlin govern directly. These regions coordinate state policy at a more local level, managing everything from school oversight to environmental protection.",
    path: "/geojson/regions/adm/DEU_ADM2.json",
    geography: "Germany",
    topics: ["Administrative Regions", "Germany", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 38,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "deu-adm3",
    title: "Germany · Districts (3rd level)",
    description: "Germany's 428 districts (Kreise) form the backbone of local administration, divided between rural districts (Landkreise) and urban districts (Kreisfreie Städte). These districts manage local services from waste collection to public transport. The district system dates back to Prussian administrative reforms and remains a cornerstone of German governance.",
    path: "/geojson/regions/adm/DEU_ADM3.json",
    geography: "Germany",
    topics: ["Administrative Regions", "Germany", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 428,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "esp-adm1",
    title: "Spain · Autonomous Communities (1st level)",
    description: "Spain's 19 autonomous communities (comunidades autónomas), including regions like Catalonia, Andalusia, Madrid, and the Basque Country.",
    path: "/geojson/regions/adm/ESP_ADM1.json",
    geography: "Spain",
    topics: ["Administrative Regions", "Spain", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 19,
    propertyKeys: ["Name"],
  },
  {
    id: "esp-adm2",
    title: "Spain · Provinces (2nd level)",
    description: "Spain's 52 provinces, the traditional administrative divisions within autonomous communities.",
    path: "/geojson/regions/adm/ESP_ADM2.json",
    geography: "Spain",
    topics: ["Administrative Regions", "Spain", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 52,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "est-adm1",
    title: "Estonia · Counties (1st level)",
    description: "Estonia's 15 counties (maakonnad), the first-level administrative divisions.",
    path: "/geojson/regions/adm/EST_ADM1.json",
    geography: "Estonia",
    topics: ["Administrative Regions", "Estonia", "Baltic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 15,
    propertyKeys: ["Name"],
  },
  {
    id: "est-adm2",
    title: "Estonia · Municipalities (2nd level)",
    description: "Estonia's 214 municipalities (omavalitsused), the primary local government units.",
    path: "/geojson/regions/adm/EST_ADM2.json",
    geography: "Estonia",
    topics: ["Administrative Regions", "Estonia", "Baltic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 214,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "fin-adm1",
    title: "Finland · Regions (1st level)",
    description: "Finland's 19 regions (maakunnat), the first-level administrative divisions.",
    path: "/geojson/regions/adm/FIN_ADM1.json",
    geography: "Finland",
    topics: ["Administrative Regions", "Finland", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 19,
    propertyKeys: ["Name"],
  },
  {
    id: "fin-adm2",
    title: "Finland · Sub-regions (2nd level)",
    description: "Finland's 70 sub-regions (seutukunnat), intermediate administrative areas.",
    path: "/geojson/regions/adm/FIN_ADM2.json",
    geography: "Finland",
    topics: ["Administrative Regions", "Finland", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 70,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "fin-adm3",
    title: "Finland · Municipalities (3rd level)",
    description: "Finland's 312 municipalities (kunnat), the primary local government units.",
    path: "/geojson/regions/adm/FIN_ADM3.json",
    geography: "Finland",
    topics: ["Municipalities", "Finland", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 312,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "fra-adm1",
    title: "France · Regions (1st level)",
    description: "France's 13 metropolitan regions after the 2016 reforms, including Île-de-France, Nouvelle-Aquitaine, and Provence-Alpes-Côte d'Azur.",
    path: "/geojson/regions/adm/FRA_ADM1.json",
    geography: "France",
    topics: ["Administrative Regions", "France", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 13,
    propertyKeys: ["Name"],
  },
  {
    id: "fra-adm2",
    title: "France · Departments (2nd level)",
    description: "France's 96 departments (départements), the primary administrative divisions within regions.",
    path: "/geojson/regions/adm/FRA_ADM2.json",
    geography: "France",
    topics: ["Administrative Regions", "France", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 96,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "fra-adm3",
    title: "France · Arrondissements (3rd level)",
    description: "France's 320 arrondissements, subdivisions of departments.",
    path: "/geojson/regions/adm/FRA_ADM3.json",
    geography: "France",
    topics: ["Administrative Regions", "France", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 320,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "grc-adm1",
    title: "Greece · Regions (1st level)",
    description: "Greece's 8 administrative regions, including Attica, Central Macedonia, Thessaly, and the Peloponnese.",
    path: "/geojson/regions/adm/GRC_ADM1.json",
    geography: "Greece",
    topics: ["Administrative Regions", "Greece", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 8,
    propertyKeys: ["Name"],
  },
  {
    id: "grc-adm2",
    title: "Greece · Regional Units (2nd level)",
    description: "Greece's 14 regional units (perifereiakes enotites), administrative subdivisions of regions.",
    path: "/geojson/regions/adm/GRC_ADM2.json",
    geography: "Greece",
    topics: ["Administrative Regions", "Greece", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 14,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "grc-adm3",
    title: "Greece · Municipalities (3rd level)",
    description: "Greece's 326 municipalities (dimoi) after the Kallikratis reform of 2011.",
    path: "/geojson/regions/adm/GRC_ADM3.json",
    geography: "Greece",
    topics: ["Municipalities", "Greece", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 326,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "grl-adm1",
    title: "Greenland · Municipalities (1st level)",
    description: "Greenland's 6 municipalities (kommuniat), including Sermersooq (which contains Nuuk, the capital).",
    path: "/geojson/regions/adm/GRL_ADM1.json",
    geography: "Greenland",
    topics: ["Administrative Regions", "Greenland", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 6,
    propertyKeys: ["Name"],
  },
  {
    id: "hrv-adm1",
    title: "Croatia · Counties (1st level)",
    description: "Croatia's 21 counties (županije), including the capital city of Zagreb as a separate entity.",
    path: "/geojson/regions/adm/HRV_ADM1.json",
    geography: "Croatia",
    topics: ["Administrative Regions", "Croatia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 21,
    propertyKeys: ["Name"],
  },
  {
    id: "hrv-adm2",
    title: "Croatia · Municipalities and Cities (2nd level)",
    description: "Croatia's 560 local government units, comprising municipalities and cities.",
    path: "/geojson/regions/adm/HRV_ADM2.json",
    geography: "Croatia",
    topics: ["Administrative Regions", "Croatia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 560,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "hun-adm1",
    title: "Hungary · Counties (1st level)",
    description: "Hungary's 19 counties (vármegyék) plus Budapest as a separate county-level city.",
    path: "/geojson/regions/adm/HUN_ADM1.json",
    geography: "Hungary",
    topics: ["Administrative Regions", "Hungary", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 19,
    propertyKeys: ["Name"],
  },
  {
    id: "hun-adm2",
    title: "Hungary · Districts (2nd level)",
    description: "Hungary's 198 districts (járások), administrative subdivisions within counties.",
    path: "/geojson/regions/adm/HUN_ADM2.json",
    geography: "Hungary",
    topics: ["Administrative Regions", "Hungary", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 198,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "irl-adm1",
    title: "Ireland · Provinces (1st level)",
    description: "Ireland's 4 historic provinces: Leinster, Munster, Connacht, and Ulster (partial).",
    path: "/geojson/regions/adm/IRL_ADM1.json",
    geography: "Ireland",
    topics: ["Administrative Regions", "Ireland", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 4,
    propertyKeys: ["Name"],
  },
  {
    id: "ita-adm1",
    title: "Italy · Regions (1st level)",
    description: "Italy's 5 autonomous regions with special status, including Sicily, Sardinia, Trentino-Alto Adige, Friuli-Venezia Giulia, and Aosta Valley.",
    path: "/geojson/regions/adm/ITA_ADM1.json",
    geography: "Italy",
    topics: ["Administrative Regions", "Italy", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 5,
    propertyKeys: ["Name"],
  },
  {
    id: "ita-adm2",
    title: "Italy · Metropolitan Cities and Provinces (2nd level)",
    description: "Italy's 20 provinces and metropolitan cities, intermediate administrative divisions.",
    path: "/geojson/regions/adm/ITA_ADM2.json",
    geography: "Italy",
    topics: ["Administrative Regions", "Italy", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 20,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "ita-adm3",
    title: "Italy · Communes (3rd level)",
    description: "Italy's 107 communes (comuni) in this administrative dataset.",
    path: "/geojson/regions/adm/ITA_ADM3.json",
    geography: "Italy",
    topics: ["Municipalities", "Italy", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 107,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "ltu-adm1",
    title: "Lithuania · Counties (1st level)",
    description: "Lithuania's 10 counties (apskritys) were established after independence from the Soviet Union. These counties coordinate national government services but have limited self-governance. They serve primarily as administrative divisions rather than autonomous regions, reflecting Lithuania's relatively centralized government structure.",
    path: "/geojson/regions/adm/LTU_ADM1.json",
    geography: "Lithuania",
    topics: ["Administrative Regions", "Lithuania", "Baltic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 10,
    propertyKeys: ["Name"],
  },
  {
    id: "ltu-adm2",
    title: "Lithuania · Municipalities (2nd level)",
    description: "Lithuania's 60 municipalities (savivaldybės) are the true centers of local government, with elected councils and mayors. These municipalities range from Vilnius with over 500,000 people to small rural districts. They manage everything from schools to social services with significant autonomy.",
    path: "/geojson/regions/adm/LTU_ADM2.json",
    geography: "Lithuania",
    topics: ["Municipalities", "Lithuania", "Baltic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 60,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "ltu-adm3",
    title: "Lithuania · Elderships (3rd level)",
    description: "Lithuania's 543 elderships (seniūnijos) are the smallest administrative units, often encompassing just a few villages or a neighborhood in larger cities. These elderships provide a direct link between citizens and local government. They're essential for delivering services in Lithuania's rural areas.",
    path: "/geojson/regions/adm/LTU_ADM3.json",
    geography: "Lithuania",
    topics: ["Administrative Regions", "Lithuania", "Baltic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 543,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "lva-adm1",
    title: "Latvia · Regions (1st level)",
    description: "Latvia's 43 municipalities and cities with special status represent a major reform from 2021 that consolidated over 100 previous units. This dramatic restructuring aimed to create more efficient local governments. The reform has been controversial, with debates about preserving local identity versus administrative efficiency.",
    path: "/geojson/regions/adm/LVA_ADM1.json",
    geography: "Latvia",
    topics: ["Administrative Regions", "Latvia", "Baltic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 43,
    propertyKeys: ["Name"],
  },
  {
    id: "lva-adm2",
    title: "Latvia · Municipalities (2nd level)",
    description: "Latvia's 589 local administrative units include parishes (pagasti) and towns that form subdivisions within the larger municipalities. These units preserve local identity and help deliver services in a country where much of the population is concentrated in Riga. They maintain connections to Latvia's rural heritage.",
    path: "/geojson/regions/adm/LVA_ADM2.json",
    geography: "Latvia",
    topics: ["Municipalities", "Latvia", "Baltic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 589,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "mkd-adm1",
    title: "North Macedonia · Statistical Regions (1st level)",
    description: "North Macedonia's 8 statistical regions were created for planning and EU integration rather than governance. These regions group municipalities for economic development and statistical purposes. They reflect North Macedonia's diverse geography from the mountainous west to the Vardar valley in the center.",
    path: "/geojson/regions/adm/MKD_ADM1.json",
    geography: "North Macedonia",
    topics: ["Administrative Regions", "North Macedonia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 8,
    propertyKeys: ["Name"],
  },
  {
    id: "mkd-adm2",
    title: "North Macedonia · Municipalities (2nd level)",
    description: "North Macedonia's 84 municipalities (opštini) are the primary units of local government in this small Balkan nation. The capital Skopje is divided into 10 municipalities, while rural areas often have just a single municipality per town. The system reflects both urban concentration and rural dispersion in a country of just 2 million people.",
    path: "/geojson/regions/adm/MKD_ADM2.json",
    geography: "North Macedonia",
    topics: ["Municipalities", "North Macedonia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 84,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "mne-adm1",
    title: "Montenegro · Municipalities (1st level)",
    description: "Montenegro's 23 municipalities (opštine) serve as both the first and primary level of administration in this mountainous Adriatic nation. These municipalities govern everything from coastal resorts like Budva to mountain villages near the Albanian border. Despite having no intermediate administrative level, Montenegro's small size makes this system workable.",
    path: "/geojson/regions/adm/MNE_ADM1.json",
    geography: "Montenegro",
    topics: ["Administrative Regions", "Montenegro", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 23,
    propertyKeys: ["Name"],
  },
  {
    id: "nld-adm1",
    title: "Netherlands · Provinces (1st level)",
    description: "The Netherlands' 12 provinces balance historic identities with modern governance needs. From Friesland with its own language to Zeeland's delta islands, each province has distinct character. These provinces manage water boards (crucial in a country largely below sea level), regional planning, and environmental protection.",
    path: "/geojson/regions/adm/NLD_ADM1.json",
    geography: "Netherlands",
    topics: ["Administrative Regions", "Netherlands", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 12,
    propertyKeys: ["Name"],
  },
  {
    id: "nld-adm2",
    title: "Netherlands · Municipalities (2nd level)",
    description: "The Netherlands' 344 municipalities (gemeenten) are the workhorses of Dutch local government, managing everything from housing to welfare services. Dutch municipalities have been steadily consolidating - there were over 1,000 in 1850. The trend toward larger municipalities continues as smaller communities merge to share resources.",
    path: "/geojson/regions/adm/NLD_ADM2.json",
    geography: "Netherlands",
    topics: ["Municipalities", "Netherlands", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 344,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "pan-adm1",
    title: "Panama · Provinces (1st level)",
    description: "Panama's 13 provinces and indigenous comarcas stretch from the Caribbean to the Pacific across the narrow isthmus. The comarcas have special autonomous status for indigenous peoples like the Guna and Ngäbe-Buglé. From the cosmopolitan Panama Province to the rainforests of Darién, these divisions showcase the country's diversity.",
    path: "/geojson/regions/adm/PAN_ADM1.json",
    geography: "Panama",
    topics: ["Administrative Regions", "Panama", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 13,
    propertyKeys: ["Name"],
  },
  {
    id: "pan-adm2",
    title: "Panama · Districts (2nd level)",
    description: "Panama's 76 districts (distritos) form intermediate administrative units between provinces and the smallest subdivisions. These districts often follow natural geographic boundaries like rivers and mountain ranges. They help organize services across Panama's challenging terrain, from coastal lowlands to mountainous interiors.",
    path: "/geojson/regions/adm/PAN_ADM2.json",
    geography: "Panama",
    topics: ["Administrative Regions", "Panama", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 76,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "pan-adm3",
    title: "Panama · Corregimientos (3rd level)",
    description: "Panama's 632 corregimientos represent the most local level of government, often encompassing just a single town or cluster of rural communities. These units are essential for delivering services across Panama's diverse landscape. In rural areas, corregimientos may cover vast territories with scattered populations.",
    path: "/geojson/regions/adm/PAN_ADM3.json",
    geography: "Panama",
    topics: ["Administrative Regions", "Panama", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 632,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "pol-adm1",
    title: "Poland · Voivodeships (1st level)",
    description: "Poland's 16 voivodeships (województwa) largely follow historic regional boundaries, from Silesia in the southwest to Masovia around Warsaw. These regions gained significant autonomy after Poland's democratic transition in 1989. Each voivodeship has an elected assembly and manages regional development, education, and healthcare.",
    path: "/geojson/regions/adm/POL_ADM1.json",
    geography: "Poland",
    topics: ["Administrative Regions", "Poland", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 16,
    propertyKeys: ["Name"],
  },
  {
    id: "pol-adm2",
    title: "Poland · Counties (2nd level)",
    description: "Poland's 380 counties (powiaty) include both land counties and cities with county rights. This two-tier system allows larger cities to manage their own services while rural areas band together. The county structure dates back to the 14th century, though it's been reformed multiple times.",
    path: "/geojson/regions/adm/POL_ADM2.json",
    geography: "Poland",
    topics: ["Administrative Regions", "Poland", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 380,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "pri-adm2",
    title: "Puerto Rico · Municipalities (2nd level)",
    description: "Puerto Rico's 78 municipalities function as the primary units of local government across the island. Each municipality has an elected mayor and assembly. From San Juan's urban sprawl to mountainous interior towns, these municipalities manage local services while navigating Puerto Rico's unique status as a U.S. territory.",
    path: "/geojson/regions/adm/PRI_ADM2.json",
    geography: "Puerto Rico",
    topics: ["Municipalities", "Puerto Rico", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 78,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "pri-adm3",
    title: "Puerto Rico · Barrios (3rd level)",
    description: "Puerto Rico's 901 barrios are traditional subdivisions that date back to Spanish colonial times. These barrios range from dense urban neighborhoods to rural agricultural areas. While they have limited administrative function today, barrios remain important for cultural identity and local community organization.",
    path: "/geojson/regions/adm/PRI_ADM3.json",
    geography: "Puerto Rico",
    topics: ["Administrative Regions", "Puerto Rico", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 901,
    propertyKeys: ["Name", "District", "Province"],
  },
  {
    id: "prt-adm1",
    title: "Portugal · Districts (1st level)",
    description: "Portugal's 20 districts are traditional divisions dating back to 1835, though their administrative importance has diminished since regional authorities were created. These districts stretch from Viana do Castelo in the north to Faro in the Algarve. They remain used for some national government services and statistical purposes.",
    path: "/geojson/regions/adm/PRT_ADM1.json",
    geography: "Portugal",
    topics: ["Administrative Regions", "Portugal", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 20,
    propertyKeys: ["Name"],
  },
  {
    id: "prt-adm2",
    title: "Portugal · Municipalities (2nd level)",
    description: "Portugal's 311 municipalities (concelhos) are the true centers of local government, with elected assemblies and mayors. These municipalities range from Lisbon with over 500,000 residents to tiny interior communities. Portuguese municipalities have significant autonomy over local services and urban planning.",
    path: "/geojson/regions/adm/PRT_ADM2.json",
    geography: "Portugal",
    topics: ["Municipalities", "Portugal", "Western Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 311,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "rou-adm1",
    title: "Romania · Counties (1st level)",
    description: "Romania's 42 counties (județe) include 41 territorial counties plus Bucharest as a separate administrative unit. These counties were established in 1968 and largely follow historic regional boundaries. From the Carpathian Mountains to the Danube Delta, each county encompasses distinct geographic and cultural regions.",
    path: "/geojson/regions/adm/ROU_ADM1.json",
    geography: "Romania",
    topics: ["Administrative Regions", "Romania", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 42,
    propertyKeys: ["Name"],
  },
  {
    id: "rus-adm1",
    title: "Russia · Federal Subjects (1st level)",
    description: "Russia's 83 federal subjects span 11 time zones and include republics with ethnic autonomy, krais, oblasts, autonomous okrugs, federal cities (Moscow and St. Petersburg), and one autonomous oblast. This diverse system reflects Russia's vast size and ethnic complexity. Federal subjects range from tiny Ingushetia to the enormous Sakha Republic, larger than India. Includes local Russian names (Русский).",
    path: "/geojson/regions/adm/RUS_ADM1.json",
    geography: "Russia",
    topics: ["Administrative Regions", "Russia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 83,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },
  {
    id: "srb-adm1",
    title: "Serbia · Districts (1st level)",
    description: "Serbia's 25 districts include the City of Belgrade and the Autonomous Province of Vojvodina. While districts have limited administrative powers, they're important for coordinating services and statistics. The district system balances Serbia's urban centers with rural agricultural regions in the Šumadija and across the Pannonian Plain.",
    path: "/geojson/regions/adm/SRB_ADM1.json",
    geography: "Serbia",
    topics: ["Administrative Regions", "Serbia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 25,
    propertyKeys: ["Name"],
  },
  {
    id: "srb-adm2",
    title: "Serbia · Municipalities (2nd level)",
    description: "Serbia's 145 municipalities and cities form the primary units of local government. Cities with over 100,000 residents have special status and more responsibilities. These units manage local services across Serbia's diverse landscape, from the Danube plain to mountainous regions bordering Kosovo.",
    path: "/geojson/regions/adm/SRB_ADM2.json",
    geography: "Serbia",
    topics: ["Municipalities", "Serbia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 145,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "svk-adm1",
    title: "Slovakia · Regions (1st level)",
    description: "Slovakia's 8 regions (kraje) were established in 1996 to comply with EU structural policies. These regions follow historic territorial divisions and geographic features. From Bratislava region on the Austrian border to mountainous Žilina in the north, each region has its own economic profile and cultural traditions.",
    path: "/geojson/regions/adm/SVK_ADM1.json",
    geography: "Slovakia",
    topics: ["Administrative Regions", "Slovakia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 8,
    propertyKeys: ["Name"],
  },
  {
    id: "svk-adm2",
    title: "Slovakia · Districts (2nd level)",
    description: "Slovakia's 79 districts (okresy) serve as intermediate administrative units, organizing courts, police, and other state services. These districts often correspond to natural geographic regions and historic market towns. They're essential for coordinating governance in Slovakia's mountainous terrain.",
    path: "/geojson/regions/adm/SVK_ADM2.json",
    geography: "Slovakia",
    topics: ["Administrative Regions", "Slovakia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 79,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "svn-adm1",
    title: "Slovenia · Statistical Regions (1st level)",
    description: "Slovenia's 2 cohesion regions (Western and Eastern Slovenia) are purely statistical units created for EU regional policy purposes. These regions have no administrative function or elected bodies. They simply divide the country roughly in half for development planning and accessing EU funds.",
    path: "/geojson/regions/adm/SVN_ADM1.json",
    geography: "Slovenia",
    topics: ["Administrative Regions", "Slovenia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 2,
    propertyKeys: ["Name"],
  },
  {
    id: "svn-adm2",
    title: "Slovenia · Municipalities (2nd level)",
    description: "Slovenia's 213 municipalities (občine) are the only real level of local government in this small Alpine nation. Municipalities gained significant autonomy after independence in 1991. From Ljubljana with 280,000 residents to tiny alpine communes with just a few hundred people, these units manage local services with elected mayors and councils.",
    path: "/geojson/regions/adm/SVN_ADM2.json",
    geography: "Slovenia",
    topics: ["Municipalities", "Slovenia", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 213,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "swe-adm1",
    title: "Sweden · Counties (1st level)",
    description: "Sweden's 21 counties (län) serve as regional administrative units for national government services. Each county has a governor appointed by the central government plus an elected regional council. These counties range from densely populated Stockholm to the vast, sparsely inhabited northern counties above the Arctic Circle.",
    path: "/geojson/regions/adm/SWE_ADM1.json",
    geography: "Sweden",
    topics: ["Administrative Regions", "Sweden", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 21,
    propertyKeys: ["Name"],
  },
  {
    id: "swe-adm2",
    title: "Sweden · Municipalities (2nd level)",
    description: "Sweden's 290 municipalities (kommuner) are the foundation of Swedish local government, responsible for schools, elderly care, and urban planning. Swedish municipalities have strong autonomy and tax-raising powers. They range from Stockholm with nearly a million residents to small northern municipalities with under 3,000 people.",
    path: "/geojson/regions/adm/SWE_ADM2.json",
    geography: "Sweden",
    topics: ["Municipalities", "Sweden", "Nordic Countries"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 290,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "ukr-adm1",
    title: "Ukraine · Oblasts (1st level)",
    description: "Ukraine's 27 oblasts represent the primary administrative divisions of Europe's second-largest country by area. These regions include the capital Kyiv with special status and the Autonomous Republic of Crimea. Ukrainian oblasts range from industrial centers in the Donbas to agricultural regions and the Carpathian Mountains. Includes local Ukrainian names (Українська).",
    path: "/geojson/regions/adm/UKR_ADM1.json",
    geography: "Ukraine",
    topics: ["Administrative Regions", "Ukraine", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 27,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },
  {
    id: "ukr-adm2",
    title: "Ukraine · Districts (2nd level)",
    description: "Ukraine's 495 districts (raions) underwent massive reform in 2020, consolidating from over 450 previous units. This reform created larger, more efficient districts. The districts organize local state administration across Ukraine's diverse landscape of steppes, forests, and mountains.",
    path: "/geojson/regions/adm/UKR_ADM2.json",
    geography: "Ukraine",
    topics: ["Administrative Regions", "Ukraine", "Eastern Europe"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 495,
    propertyKeys: ["Name", "Province"],
  },
  {
    id: "usa-adm1",
    title: "United States · States (1st level)",
    description: "The 56 U.S. states and territories include the 50 states, District of Columbia, and 5 permanently inhabited territories (Puerto Rico, U.S. Virgin Islands, Guam, American Samoa, Northern Mariana Islands). Each state has its own constitution and government structure. This federal system gives states remarkable autonomy over education, criminal law, and many other policy areas.",
    path: "/geojson/regions/adm/USA_ADM1.json",
    geography: "United States",
    topics: ["Administrative Regions", "United States", "North America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 56,
    propertyKeys: ["Name"],
  },
  {
    id: "vir-adm3",
    title: "U.S. Virgin Islands · Subdistricts (3rd level)",
    description: "The U.S. Virgin Islands' 20 subdistricts are distributed across three main islands - St. Croix, St. Thomas, and St. John. These subdivisions help organize services and elections across the territory. Despite their small total population of about 100,000, the islands maintain distinct local identities.",
    path: "/geojson/regions/adm/VIR_ADM3.json",
    geography: "U.S. Virgin Islands",
    topics: ["Administrative Regions", "U.S. Virgin Islands", "Latin America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 20,
    propertyKeys: ["Name"],
  },

  {
    id: "arg-adm1",
    title: "Argentina · Provinces (1st level)",
    description: "Argentina's 23 provinces plus the autonomous city of Buenos Aires span from the subtropical north to Patagonia and Tierra del Fuego in the south. These provinces have significant autonomy under Argentina's federal system, managing education, healthcare, and local police. From wine-producing Mendoza at the Andes' foothills to windswept Santa Cruz, each province showcases distinct landscapes and cultural identities.",
    path: "/geojson/regions/adm/ARG_ADM1.json",
    geography: "Argentina",
    topics: ["Administrative Regions","Argentina","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 23,
    propertyKeys: ["Name"],
  },

  {
    id: "arg-adm2",
    title: "Argentina · Departments (2nd level)",
    description: "Argentina's departments (departamentos) serve as intermediate administrative units within provinces, totaling over 370 across the country. These departments vary dramatically in size and population, from dense urban areas around Buenos Aires to vast, sparsely-populated territories in Patagonia. They coordinate provincial governance across Argentina's diverse geography of pampas, mountains, and coastal regions.",
    path: "/geojson/regions/adm/ARG_ADM2.json",
    geography: "Argentina",
    topics: ["Administrative Regions","Argentina","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 525,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "bgd-adm1",
    title: "Bangladesh · Divisions (1st level)",
    description: "Bangladesh's 8 administrative divisions organize one of the world's most densely populated countries. From Dhaka Division's urban sprawl to the mangrove forests of Khulna, these divisions coordinate services across the Ganges-Brahmaputra delta. Each division balances the challenges of monsoon flooding, dense population, and economic development in this South Asian nation. Includes local Bengali names (বাংলা).",
    path: "/geojson/regions/adm/BGD_ADM1.json",
    geography: "Bangladesh",
    topics: ["Administrative Regions","Bangladesh","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 8,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },

  {
    id: "bgd-adm2",
    title: "Bangladesh · Districts (2nd level)",
    description: "Bangladesh's 64 districts (zila) form the primary units of regional administration across the country's river-crossed landscape. These districts manage everything from agriculture in the fertile delta to industry in urban centers like Dhaka and Chittagong. District boundaries often follow major rivers that shape life in Bangladesh. Includes local Bengali names (বাংলা).",
    path: "/geojson/regions/adm/BGD_ADM2.json",
    geography: "Bangladesh",
    topics: ["Administrative Regions","Bangladesh","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 64,
    propertyKeys: ["Name", "Province", "LocalName", "LocalScript", "LocalLang"],
  },

  {
    id: "bgd-adm3",
    title: "Bangladesh · Subdistricts (3rd level)",
    description: "Bangladesh's 544 subdistricts (upazila) represent the most local level of government administration. These units deliver services from healthcare to education across densely populated rural areas and growing cities. Subdistricts are essential for coordinating development in a country where most people still live in villages.",
    path: "/geojson/regions/adm/BGD_ADM3.json",
    geography: "Bangladesh",
    topics: ["Administrative Regions","Bangladesh","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 544,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "bol-adm1",
    title: "Bolivia · Departments (1st level)",
    description: "Bolivia's 9 departments stretch from the Andean Altiplano to the Amazon rainforest, showcasing extreme geographic diversity. La Paz sits at 3,650 meters elevation while lowland Beni barely reaches 200 meters. These departments gained greater autonomy after Bolivia's 2009 constitution, reflecting the country's indigenous majority and regional identities.",
    path: "/geojson/regions/adm/BOL_ADM1.json",
    geography: "Bolivia",
    topics: ["Administrative Regions","Bolivia","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 9,
    propertyKeys: ["Name"],
  },

  {
    id: "bol-adm2",
    title: "Bolivia · Provinces (2nd level)",
    description: "Bolivia's 112 provinces subdivide the departments across mountains, valleys, and tropical lowlands. These provinces often correspond to distinct ecological zones and indigenous territories. From mining provinces in Potosí to agricultural regions in Santa Cruz, they reflect Bolivia's economic and cultural diversity.",
    path: "/geojson/regions/adm/BOL_ADM2.json",
    geography: "Bolivia",
    topics: ["Administrative Regions","Bolivia","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 110,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "bol-adm3",
    title: "Bolivia · Municipalities (3rd level)",
    description: "Bolivia's 327 municipalities are the foundation of local government, each with elected mayors and councils. These municipalities range from La Paz with nearly a million residents to tiny Andean communities. They're crucial for delivering services across Bolivia's challenging terrain and implementing indigenous autonomy rights.",
    path: "/geojson/regions/adm/BOL_ADM3.json",
    geography: "Bolivia",
    topics: ["Administrative Regions","Bolivia","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 339,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "bra-adm1",
    title: "Brazil · States (1st level)",
    description: "Brazil's 26 states plus the Federal District encompass the world's fifth-largest country by area. From tropical Amazonas covering 1.5 million km² to tiny Sergipe on the Atlantic coast, each state has its own constitution and elected government. These states reflect Brazil's regional diversity, from the industrial south to the Amazon north.",
    path: "/geojson/regions/adm/BRA_ADM1.json",
    geography: "Brazil",
    topics: ["Administrative Regions","Brazil","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 27,
    propertyKeys: ["Name"],
  },

  {
    id: "btn-adm1",
    title: "Bhutan · Dzongkhags (1st level)",
    description: "Bhutan's 20 dzongkhags (districts) organize this Himalayan Buddhist kingdom from valleys at 100 meters to peaks above 7,000 meters. Each dzongkhag is administered from a dzong (fortress-monastery), blending spiritual and temporal authority. These districts manage Bhutan's unique development philosophy of Gross National Happiness across mountainous terrain.",
    path: "/geojson/regions/adm/BTN_ADM1.json",
    geography: "Bhutan",
    topics: ["Administrative Regions","Bhutan","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 20,
    propertyKeys: ["Name"],
  },

  {
    id: "btn-adm2",
    title: "Bhutan · Gewogs (2nd level)",
    description: "Bhutan's 205 gewogs (blocks) form groups of villages that elect their own leaders. These gewogs preserve local governance traditions while implementing national policies. They're essential for managing rural development in a country where most people still practice subsistence farming in remote mountain valleys.",
    path: "/geojson/regions/adm/BTN_ADM2.json",
    geography: "Bhutan",
    topics: ["Administrative Regions","Bhutan","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 205,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "chl-adm1",
    title: "Chile · Regions (1st level)",
    description: "Chile's 16 regions stretch 4,300 km from the Atacama Desert to the fjords of Patagonia. The 2018 reforms gave regions greater autonomy with elected governors. From copper-mining Antofagasta to wine-producing Maule, each region has distinct economic profiles shaped by Chile's extreme north-south geography.",
    path: "/geojson/regions/adm/CHL_ADM1.json",
    geography: "Chile",
    topics: ["Administrative Regions","Chile","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 16,
    propertyKeys: ["Name"],
  },

  {
    id: "chl-adm2",
    title: "Chile · Provinces (2nd level)",
    description: "Chile's 56 provinces subdivide the regions along Chile's narrow coastal strip between the Andes and Pacific. These provinces often follow natural boundaries of valleys and watersheds. They coordinate services across diverse landscapes from desert to glaciers within just 177 km average width.",
    path: "/geojson/regions/adm/CHL_ADM2.json",
    geography: "Chile",
    topics: ["Administrative Regions","Chile","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 56,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "chl-adm3",
    title: "Chile · Communes (3rd level)",
    description: "Chile's 346 communes (comunas) are the primary units of local government, each with elected municipal councils. These communes manage local services from urban Santiago to remote villages in Tierra del Fuego. They're crucial for delivering services across Chile's extreme geography and sparse population in the south.",
    path: "/geojson/regions/adm/CHL_ADM3.json",
    geography: "Chile",
    topics: ["Administrative Regions","Chile","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 345,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "chn-adm1",
    title: "China · Provinces (1st level)",
    description: "China's 34 provincial-level divisions include 22 provinces, 5 autonomous regions, 4 municipalities, and 2 special administrative regions. From tiny Macau to vast Xinjiang (1.66 million km²), these units reflect China's size and diversity. Provinces have managed regional development while balancing central government control with local needs.",
    path: "/geojson/regions/adm/CHN_ADM1.json",
    geography: "China",
    topics: ["Administrative Regions","China","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 34,
    propertyKeys: ["Name"],
  },

  {
    id: "col-adm1",
    title: "Colombia · Departments (1st level)",
    description: "Colombia's 32 departments plus Bogotá Capital District span from Caribbean beaches to Amazon rainforest and Andean peaks. These departments gained autonomy in the 1991 constitution with elected governors. From coffee-producing Caldas to oil-rich Casanare, each department reflects Colombia's geographic and economic diversity.",
    path: "/geojson/regions/adm/COL_ADM1.json",
    geography: "Colombia",
    topics: ["Administrative Regions","Colombia","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 33,
    propertyKeys: ["Name"],
  },

  {
    id: "col-adm2",
    title: "Colombia · Municipalities (2nd level)",
    description: "Colombia's 1,101 municipalities form the backbone of local government across diverse terrain from coastal lowlands to mountain valleys. These municipalities manage local services while navigating Colombia's complex geography and history. They range from Bogotá with 8 million people to tiny Amazon river communities.",
    path: "/geojson/regions/adm/COL_ADM2.json",
    geography: "Colombia",
    topics: ["Administrative Regions","Colombia","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 1122,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "dom-adm1",
    title: "Dominican Republic · Provinces (1st level)",
    description: "The Dominican Republic's 31 provinces plus the National District cover the eastern two-thirds of Hispaniola island. These provinces stretch from Caribbean beaches to central highlands and the Samaná Peninsula. The province system dates to the 19th century and has been reformed multiple times to manage this Caribbean nation's development.",
    path: "/geojson/regions/adm/DOM_ADM1.json",
    geography: "Dominican Republic",
    topics: ["Administrative Regions","Dominican Republic","Caribbean"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 32,
    propertyKeys: ["Name"],
  },

  {
    id: "dom-adm2",
    title: "Dominican Republic · Municipalities (2nd level)",
    description: "The Dominican Republic's 159 municipalities (municipios) govern local affairs across the island. These municipalities range from tourist destinations like Punta Cana to agricultural interior towns. They manage services from waste collection to local roads across varied Caribbean landscapes.",
    path: "/geojson/regions/adm/DOM_ADM2.json",
    geography: "Dominican Republic",
    topics: ["Administrative Regions","Dominican Republic","Caribbean"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 155,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "ecu-adm1",
    title: "Ecuador · Provinces (1st level)",
    description: "Ecuador's 24 provinces span from Pacific coast through Andean highlands to Amazon rainforest, plus the Galápagos Islands. Despite Ecuador's small size (256,000 km²), these provinces encompass remarkable biodiversity and cultural diversity. From coastal Guayas to highland Chimborazo, each province has distinct geography and traditions.",
    path: "/geojson/regions/adm/ECU_ADM1.json",
    geography: "Ecuador",
    topics: ["Administrative Regions","Ecuador","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 24,
    propertyKeys: ["Name"],
  },

  {
    id: "ecu-adm2",
    title: "Ecuador · Cantons (2nd level)",
    description: "Ecuador's 221 cantons serve as intermediate administrative units with elected mayors and councils. These cantons manage local services across Ecuador's varied terrain from coastal mangroves to volcanic highlands. They're crucial for coordinating development in one of South America's most densely populated countries.",
    path: "/geojson/regions/adm/ECU_ADM2.json",
    geography: "Ecuador",
    topics: ["Administrative Regions","Ecuador","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 223,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "gtm-adm1",
    title: "Guatemala · Departments (1st level)",
    description: "Guatemala's 22 departments stretch from Pacific beaches to Caribbean coast, encompassing Mayan highlands and tropical lowlands. These departments trace their origins to colonial intendencias and reflect Guatemala's ethnic and linguistic diversity. From indigenous-majority Totonicapán to mestizo Escuintla, each department has distinct character.",
    path: "/geojson/regions/adm/GTM_ADM1.json",
    geography: "Guatemala",
    topics: ["Administrative Regions","Guatemala","Central America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 22,
    propertyKeys: ["Name"],
  },

  {
    id: "gtm-adm2",
    title: "Guatemala · Municipalities (2nd level)",
    description: "Guatemala's 340 municipalities (municipios) form the foundation of local government, many following pre-Columbian settlement patterns. These municipalities range from Guatemala City with 3 million people to small highland villages. They're essential for managing services in a country where indigenous languages and traditions remain strong.",
    path: "/geojson/regions/adm/GTM_ADM2.json",
    geography: "Guatemala",
    topics: ["Administrative Regions","Guatemala","Central America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 342,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "idn-adm1",
    title: "Indonesia · Provinces (1st level)",
    description: "Indonesia's 34 provinces span the world's largest archipelago nation across 17,000 islands. These include special regions like Aceh with Islamic law and Papua with indigenous autonomy. From Java's dense population to sparsely inhabited provinces, they coordinate governance across exceptional geographic and cultural diversity.",
    path: "/geojson/regions/adm/IDN_ADM1.json",
    geography: "Indonesia",
    topics: ["Administrative Regions","Indonesia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 34,
    propertyKeys: ["Name"],
  },

  {
    id: "idn-adm2",
    title: "Indonesia · Regencies (2nd level)",
    description: "Indonesia's 514 regencies (kabupaten) and cities form the key level of local government below provinces. These units manage services across islands from Sumatra to Papua. The regency system helps coordinate development across Indonesia's challenges of island geography, earthquakes, and volcanoes.",
    path: "/geojson/regions/adm/IDN_ADM2_2.json",
    geography: "Indonesia",
    topics: ["Administrative Regions","Indonesia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 519,
    propertyKeys: ["Name", "Province", "OnStreetView", "Island"],
  },

  {
    id: "ind-adm1",
    title: "India · States and Territories (1st level)",
    description: "India's 36 states and union territories govern 1.4 billion people across the world's seventh-largest country. From tiny Goa to vast Rajasthan, each state has its own elected government, official languages, and cultural identity. India's federal system gives states significant autonomy over education, agriculture, and policing. Includes local Hindi names (हिन्दी).",
    path: "/geojson/regions/adm/IND_ADM1.json",
    geography: "India",
    topics: ["Administrative Regions","India","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 36,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },

  {
    id: "ind-adm2",
    title: "India · Districts (2nd level)",
    description: "India's 735 districts form the primary administrative units across the country's diverse landscape. These districts coordinate everything from rural development to urban planning. They range from densely populated urban districts to remote areas in the Himalayas and northeast hills.",
    path: "/geojson/regions/adm/IND_ADM2.json",
    geography: "India",
    topics: ["Administrative Regions","India","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 735,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "jpn-adm1",
    title: "Japan · Prefectures (1st level)",
    description: "Japan's 47 prefectures span four main islands from subtropical Okinawa to snowy Hokkaido. Each prefecture has an elected governor and assembly managing education, police, and infrastructure. From tiny Kagawa (1,876 km²) to Hokkaido (83,424 km²), prefectures balance local identity with Japan's centralized governance traditions. Includes local Japanese names (日本語).",
    path: "/geojson/regions/adm/JPN_ADM1.json",
    geography: "Japan",
    topics: ["Administrative Regions","Japan","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 47,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },

  {
    id: "jpn-adm2",
    title: "Japan · Municipalities (2nd level)",
    description: "Japan's 1,724 municipalities include cities, towns, and villages that have undergone massive consolidation since the 1950s. These municipalities manage local services from schools to waste collection. They range from Tokyo's special wards with millions of residents to depopulating rural villages in mountainous regions.",
    path: "/geojson/regions/adm/JPN_ADM2.json",
    geography: "Japan",
    topics: ["Administrative Regions","Japan","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 1742,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "kgz-adm1",
    title: "Kyrgyzstan · Regions (1st level)",
    description: "Kyrgyzstan's 7 regions (oblastlar) plus the capital Bishkek organize this mountainous Central Asian nation. Over 90% of Kyrgyzstan is mountainous, with the Tian Shan and Pamir ranges dominating. These regions coordinate governance across challenging terrain from Issyk-Kul lake basin to high-altitude valleys.",
    path: "/geojson/regions/adm/KGZ_ADM1.json",
    geography: "Kyrgyzstan",
    topics: ["Administrative Regions","Kyrgyzstan","Central Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 7,
    propertyKeys: ["Name"],
  },

  {
    id: "kgz-adm2",
    title: "Kyrgyzstan · Districts (2nd level)",
    description: "Kyrgyzstan's 44 districts (raions) subdivide the regions across mountainous terrain. These districts often follow river valleys and traditional pastoral territories. They're essential for managing services in a country where nomadic traditions persist alongside Soviet-era infrastructure.",
    path: "/geojson/regions/adm/KGZ_ADM2.json",
    geography: "Kyrgyzstan",
    topics: ["Administrative Regions","Kyrgyzstan","Central Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 41,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "khm-adm1",
    title: "Cambodia · Provinces (1st level)",
    description: "Cambodia's 25 provinces plus Phnom Penh follow boundaries dating to the French protectorate era. These provinces span from Mekong River lowlands to Cardamom Mountains and Angkor temple regions. The province system balances Cambodia's predominantly Khmer culture with minority highland groups and Vietnamese and Chinese communities.",
    path: "/geojson/regions/adm/KHM_ADM1.json",
    geography: "Cambodia",
    topics: ["Administrative Regions","Cambodia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 25,
    propertyKeys: ["Name"],
  },

  {
    id: "khm-adm2",
    title: "Cambodia · Districts (2nd level)",
    description: "Cambodia's 197 districts (srok and khan) organize local administration across the country. These districts manage services from rice-growing Battambang to coastal fishing communities. They coordinate development in a country still recovering from the Khmer Rouge era.",
    path: "/geojson/regions/adm/KHM_ADM2.json",
    geography: "Cambodia",
    topics: ["Administrative Regions","Cambodia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 197,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "khm-adm3",
    title: "Cambodia · Communes (3rd level)",
    description: "Cambodia's 1,633 communes (khum and sangkat) represent the most local government level. These communes often encompass traditional village clusters. They're crucial for delivering services in rural Cambodia where most people practice rice farming.",
    path: "/geojson/regions/adm/KHM_ADM3.json",
    geography: "Cambodia",
    topics: ["Administrative Regions","Cambodia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 1633,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "kor-adm1",
    title: "South Korea · Provinces (1st level)",
    description: "South Korea's 17 first-level divisions include 8 provinces, 1 special city (Seoul), 6 metropolitan cities, and 1 special autonomous province (Jeju). These divisions organize one of the world's most densely populated countries. From industrial Gyeonggi surrounding Seoul to agricultural Jeolla provinces, each has distinct economic character. Includes local Korean names (한국어).",
    path: "/geojson/regions/adm/KOR_ADM1.json",
    geography: "South Korea",
    topics: ["Administrative Regions","South Korea","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 17,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },

  {
    id: "kor-adm2",
    title: "South Korea · Counties and Cities (2nd level)",
    description: "South Korea's 226 counties and cities form the second administrative level across the peninsula. These units manage local affairs from densely populated metropolitan areas to rural agricultural regions. The system reflects South Korea's rapid urbanization, with most population now concentrated in major cities.",
    path: "/geojson/regions/adm/KOR_ADM2.json",
    geography: "South Korea",
    topics: ["Administrative Regions","South Korea","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 228,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "lao-adm1",
    title: "Laos · Provinces (1st level)",
    description: "Laos' 18 provinces organize this landlocked Southeast Asian nation along the Mekong River and surrounding mountains. From Phongsaly on the Chinese border to Attapeu near Cambodia, these provinces follow natural geographic regions. They coordinate governance across diverse ethnic groups in one of Asia's least densely populated countries.",
    path: "/geojson/regions/adm/LAO_ADM1.json",
    geography: "Laos",
    topics: ["Administrative Regions","Laos","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 18,
    propertyKeys: ["Name"],
  },

  {
    id: "lao-adm2",
    title: "Laos · Districts (2nd level)",
    description: "Laos' 148 districts (muang) form intermediate administrative units across mountainous terrain. These districts often follow river valleys where most settlement occurs. They're essential for managing development in a country where infrastructure remains limited and many villages lack road access.",
    path: "/geojson/regions/adm/LAO_ADM2.json",
    geography: "Laos",
    topics: ["Administrative Regions","Laos","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 148,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "lka-adm1",
    title: "Sri Lanka · Provinces (1st level)",
    description: "Sri Lanka's 9 provinces date to the 1987 Indo-Lanka Accord, though their power remains limited. These provinces span from Northern Province recovering from civil war to tourist-heavy Southern Province. They organize governance across this island nation's diverse landscape of highlands, coastal plains, and the Cultural Triangle.",
    path: "/geojson/regions/adm/LKA_ADM1.json",
    geography: "Sri Lanka",
    topics: ["Administrative Regions","Sri Lanka","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 9,
    propertyKeys: ["Name"],
  },

  {
    id: "lka-adm2",
    title: "Sri Lanka · Districts (2nd level)",
    description: "Sri Lanka's 25 districts serve as the primary administrative units, many following colonial-era boundaries. These districts coordinate everything from tea plantations in Nuwara Eliya to coastal fishing in Galle. They manage services across Sri Lanka's ethnic and religious diversity.",
    path: "/geojson/regions/adm/LKA_ADM2.json",
    geography: "Sri Lanka",
    topics: ["Administrative Regions","Sri Lanka","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 25,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "lka-adm3",
    title: "Sri Lanka · Divisional Secretariats (3rd level)",
    description: "Sri Lanka's 330+ divisional secretariats form the most local government level below districts. These units deliver services from education to land administration. They're essential for managing Sri Lanka's mix of urban areas, agricultural regions, and the recovering north and east.",
    path: "/geojson/regions/adm/LKA_ADM3.json",
    geography: "Sri Lanka",
    topics: ["Administrative Regions","Sri Lanka","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 330,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "mex-adm1",
    title: "Mexico · States (1st level)",
    description: "Mexico's 32 federal entities include 31 states plus Mexico City as the capital. These states have significant autonomy under Mexico's federal system, with their own constitutions and elected governors. From Baja California's deserts to Yucatán's tropical forests, each state reflects Mexico's remarkable diversity.",
    path: "/geojson/regions/adm/MEX_ADM1.json",
    geography: "Mexico",
    topics: ["Administrative Regions","Mexico","North America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 32,
    propertyKeys: ["Name"],
  },

  {
    id: "mng-adm1",
    title: "Mongolia · Provinces (1st level)",
    description: "Mongolia's 21 provinces (aimags) plus the capital Ulaanbaatar organize the world's most sparsely populated sovereign nation. These provinces span steppes, deserts, and mountains across 1.56 million km². From Gobi Desert provinces to forest-covered Khövsgöl, they coordinate governance for nomadic herders and growing urban populations.",
    path: "/geojson/regions/adm/MNG_ADM1.json",
    geography: "Mongolia",
    topics: ["Administrative Regions","Mongolia","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 22,
    propertyKeys: ["Name"],
  },

  {
    id: "mng-adm2",
    title: "Mongolia · Districts (2nd level)",
    description: "Mongolia's 331 districts (sums) subdivide the provinces across vast territories with tiny populations. These districts often cover thousands of square kilometers with just a few thousand people. They're essential for managing services for nomadic herders and permanent settlements across harsh continental climate.",
    path: "/geojson/regions/adm/MNG_ADM2.json",
    geography: "Mongolia",
    topics: ["Administrative Regions","Mongolia","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 339,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "mys-adm1",
    title: "Malaysia · States (1st level)",
    description: "Malaysia's 13 states plus 3 federal territories span Peninsular Malaysia and Borneo's Sabah and Sarawak. Nine states have hereditary rulers (sultans), while others have governors. From densely populated Penang to Sarawak's rainforests, each state manages resources and development within Malaysia's federal system.",
    path: "/geojson/regions/adm/MYS_ADM1.json",
    geography: "Malaysia",
    topics: ["Administrative Regions","Malaysia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 16,
    propertyKeys: ["Name"],
  },

  {
    id: "mys-adm2",
    title: "Malaysia · Districts (2nd level)",
    description: "Malaysia's 159 districts organize administration below state level across diverse terrain. These districts range from dense urban areas like Kuala Lumpur to remote interior regions in Borneo. They coordinate services across Malaysia's multicultural society and plantation, mining, and tech economies.",
    path: "/geojson/regions/adm/MYS_ADM2.json",
    geography: "Malaysia",
    topics: ["Administrative Regions","Malaysia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 159,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "mys-adm3",
    title: "Malaysia · Mukims (3rd level)",
    description: "Malaysia's 1,859 mukims form the smallest administrative units, primarily in Peninsular Malaysia. These subdivisions often follow traditional Malay community boundaries. They're important for local governance and land administration across Malaysia's mix of urban, plantation, and village areas.",
    path: "/geojson/regions/adm/MYS_ADM3.json",
    geography: "Malaysia",
    topics: ["Administrative Regions","Malaysia","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 1859,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "pak-adm1",
    title: "Pakistan · Provinces (1st level)",
    description: "Pakistan's 4 provinces plus Islamabad Capital Territory and Gilgit-Baltistan organize the world's fifth most populous country. These provinces—Punjab, Sindh, Khyber Pakhtunkhwa, and Balochistan—have distinct ethnic identities and languages. From Karakoram peaks to Indus River plains, they span diverse geography and cultures.",
    path: "/geojson/regions/adm/PAK_ADM1.json",
    geography: "Pakistan",
    topics: ["Administrative Regions","Pakistan","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 7,
    propertyKeys: ["Name"],
  },

  {
    id: "pak-adm2",
    title: "Pakistan · Districts (2nd level)",
    description: "Pakistan's 160+ districts organize local administration across varied terrain from mountains to deserts. These districts coordinate everything from agriculture in Punjab to tribal areas in the northwest. They're crucial for managing services in a rapidly urbanizing country of 240 million people.",
    path: "/geojson/regions/adm/PAK_ADM2.json",
    geography: "Pakistan",
    topics: ["Administrative Regions","Pakistan","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 126,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "pak-adm3",
    title: "Pakistan · Tehsils (3rd level)",
    description: "Pakistan's 600+ tehsils (or talukas) form the subdivision below districts. These units deliver basic services from education to land records. They're essential for governance in a country where much of the population lives in rural villages with traditional systems coexisting with modern administration.",
    path: "/geojson/regions/adm/PAK_ADM3.json",
    geography: "Pakistan",
    topics: ["Administrative Regions","Pakistan","South Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 554,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "per-adm1",
    title: "Peru · Regions (1st level)",
    description: "Peru's 25 regions (plus Lima Province) reflect the 2002 decentralization reforms. These regions span from Pacific coast through Andes highlands to Amazon rainforest. From Arequipa's volcanoes to Loreto's jungle, each region manages development across Peru's extreme topography and cultural diversity.",
    path: "/geojson/regions/adm/PER_ADM1.json",
    geography: "Peru",
    topics: ["Administrative Regions","Peru","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 26,
    propertyKeys: ["Name"],
  },

  {
    id: "per-adm2",
    title: "Peru · Provinces (2nd level)",
    description: "Peru's 196 provinces subdivide the regions across coastal desert, mountain valleys, and rainforest. These provinces often follow natural geographic boundaries. They coordinate services across Peru's challenges of difficult terrain, indigenous communities, and uneven development.",
    path: "/geojson/regions/adm/PER_ADM2.json",
    geography: "Peru",
    topics: ["Administrative Regions","Peru","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 196,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "phl-adm1",
    title: "Philippines · Regions (1st level)",
    description: "The Philippines' 17 regions organize the archipelago's 7,640 islands across three main island groups: Luzon, Visayas, and Mindanao. These administrative regions coordinate national services but have no elected governments. From Metro Manila's dense population to remote island groups, they organize governance across exceptional island diversity.",
    path: "/geojson/regions/adm/PHL_ADM1.json",
    geography: "Philippines",
    topics: ["Administrative Regions","Philippines","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 17,
    propertyKeys: ["Name"],
  },

  {
    id: "phl-adm2",
    title: "Philippines · Provinces (2nd level)",
    description: "The Philippines' 81 provinces (plus Metro Manila) form the primary elected local governments. These provinces range from tiny Batanes islands to large Mindanao provinces. They manage local services across the challenges of typhoons, earthquakes, and coordinating development across scattered islands.",
    path: "/geojson/regions/adm/PHL_ADM2.json",
    geography: "Philippines",
    topics: ["Administrative Regions","Philippines","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 86,
    propertyKeys: ["Name"],
  },

  {
    id: "phl-adm3",
    title: "Philippines · Municipalities (3rd level)",
    description: "The Philippines' 1,488 municipalities and 146 cities form the foundation of local government across the archipelago. These units range from dense urban centers like Quezon City to remote island municipalities. They're crucial for delivering services across one of the world's most disaster-prone countries.",
    path: "/geojson/regions/adm/PHL_ADM3.json",
    geography: "Philippines",
    topics: ["Administrative Regions","Philippines","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 1647,
    propertyKeys: ["Name"],
  },

  {
    id: "tha-adm1",
    title: "Thailand · Provinces (1st level)",
    description: "Thailand's 76 provinces plus Bangkok organize the kingdom from Myanmar borders to Malaysian frontier. These provinces (changwat) have governors appointed by the interior ministry. From northern Chiang Mai to southern Phuket, each province reflects Thailand's diverse geography from mountains to beaches to fertile central plains. Includes local Thai names (ไทย).",
    path: "/geojson/regions/adm/THA_ADM1.json",
    geography: "Thailand",
    topics: ["Administrative Regions","Thailand","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 77,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },

  {
    id: "tha-adm2",
    title: "Thailand · Districts (2nd level)",
    description: "Thailand's 928 districts (amphoe) subdivide provinces across the country. These districts coordinate local administration in a relatively centralized system. They manage everything from rice cultivation in the central plains to rubber plantations in the south and tourism along the coasts.",
    path: "/geojson/regions/adm/THA_ADM2.json",
    geography: "Thailand",
    topics: ["Administrative Regions","Thailand","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 928,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "twn-adm1",
    title: "Taiwan · Counties (1st level)",
    description: "Taiwan's 22 first-level divisions include special municipalities, counties, and cities across the island. These units manage services for 23 million people in an area of 36,000 km². From Taipei's urban density to rural Hualien County, they balance Taiwan's high-tech economy with agricultural traditions. Includes local Traditional Chinese names (繁體中文).",
    path: "/geojson/regions/adm/TWN_ADM1.json",
    geography: "Taiwan",
    topics: ["Administrative Regions","Taiwan","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 22,
    propertyKeys: ["Name", "LocalName", "LocalScript", "LocalLang"],
  },

  {
    id: "twn-adm2",
    title: "Taiwan · Districts (2nd level)",
    description: "Taiwan's 368 districts, townships, and county-administered cities form the second level of local government. These units manage affairs from urban Taipei districts to rural aboriginal townships in the mountains. They're crucial for delivering services across Taiwan's mountainous terrain and earthquake-prone environment.",
    path: "/geojson/regions/adm/TWN_ADM2.json",
    geography: "Taiwan",
    topics: ["Administrative Regions","Taiwan","East Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 368,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "ury-adm1",
    title: "Uruguay · Departments (1st level)",
    description: "Uruguay's 19 departments organize South America's second-smallest nation. Each department has an elected governor (intendente) and legislature. From cattle-ranching Salto to beach resort Maldonado, these departments manage Uruguay's relatively prosperous and socially advanced society.",
    path: "/geojson/regions/adm/URY_ADM1.json",
    geography: "Uruguay",
    topics: ["Administrative Regions","Uruguay","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 19,
    propertyKeys: ["Name"],
  },

  {
    id: "ury-adm2",
    title: "Uruguay · Municipalities (2nd level)",
    description: "Uruguay's 125 municipalities were created in 2010 to provide local government below the department level. These municipalities manage local services in smaller cities and rural areas. They've helped decentralize governance in Uruguay's traditionally department-centered system.",
    path: "/geojson/regions/adm/URY_ADM2.json",
    geography: "Uruguay",
    topics: ["Administrative Regions","Uruguay","South America"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 124,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "vnm-adm1",
    title: "Vietnam · Provinces (1st level)",
    description: "Vietnam's 63 provinces and 5 municipalities organize the country from Chinese border to Mekong Delta. These first-level units have significant autonomy over local development. From industrial Bắc Ninh to agricultural An Giang, they reflect Vietnam's rapid economic growth and regional diversity.",
    path: "/geojson/regions/adm/VNM_ADM1.json",
    geography: "Vietnam",
    topics: ["Administrative Regions","Vietnam","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 64,
    propertyKeys: ["Name"],
  },

  {
    id: "vnm-adm2",
    title: "Vietnam · Districts (2nd level)",
    description: "Vietnam's 705+ districts subdivide provinces across the country's S-shape from north to south. These districts coordinate local administration from Red River Delta to Central Highlands to Mekong Delta. They manage Vietnam's mix of rice paddies, industrial zones, and growing cities.",
    path: "/geojson/regions/adm/VNM_ADM2.json",
    geography: "Vietnam",
    topics: ["Administrative Regions","Vietnam","Southeast Asia"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 708,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "are-adm1",
    title: "United Arab Emirates · Emirates (1st level)",
    description: "United Arab Emirates' 7 emirates form a federation established in 1971, each ruled by its own hereditary emir. From wealthy Abu Dhabi (holding 95% of UAE's oil) to commercial Dubai to smaller northern emirates, each maintains autonomy over local affairs. The emirates showcase dramatic transformation from pearl-diving ports to global business hubs in just 50 years.",
    path: "/geojson/regions/adm/ARE_ADM1.json",
    geography: "United Arab Emirates",
    topics: ["Administrative Regions","United Arab Emirates","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 7,
    propertyKeys: ["Name"],
  },

  {
    id: "aus-adm1",
    title: "Australia · States and Territories (1st level)",
    description: "Australia's 6 states and 2 territories govern the world's sixth-largest country and continent. From tropical Queensland to temperate Tasmania, these states date to colonial federations predating 1901 independence. Western Australia alone is larger than Western Europe, while tiny Australian Capital Territory holds Canberra. Each state maintains significant autonomy over education, health, and police.",
    path: "/geojson/regions/adm/AUS_ADM1.json",
    geography: "Australia",
    topics: ["Administrative Regions","Australia","Oceania"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 9,
    propertyKeys: ["Name"],
  },

  {
    id: "aus-adm2",
    title: "Australia · Local Government Areas (2nd level)",
    description: "Australia's 547 local government areas manage services across vast distances from coastal cities to outback territories. These LGAs range from Sydney with 5 million residents to tiny Outback councils covering areas larger than European countries with just hundreds of people. They're crucial for managing Australia's highly urbanized population and remote rural communities.",
    path: "/geojson/regions/adm/AUS_ADM2.json",
    geography: "Australia",
    topics: ["Administrative Regions","Australia","Oceania"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 547,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "bwa-adm1",
    title: "Botswana · Districts (1st level)",
    description: "Botswana's 10 districts organize one of Africa's most sparsely populated countries, ranging from the Kalahari Desert to the Okavango Delta wetlands. These districts gained their structure after independence in 1966, balancing traditional kgotla governance with modern administration. From populous South East District around Gaborone to remote Ghanzi in the west, they manage services across Botswana's diamond wealth and safari economy.",
    path: "/geojson/regions/adm/BWA_ADM1.json",
    geography: "Botswana",
    topics: ["Administrative Regions","Botswana","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 10,
    propertyKeys: ["Name"],
  },

  {
    id: "bwa-adm2",
    title: "Botswana · Sub-districts (2nd level)",
    description: "Botswana's 25 sub-districts provide more localized administration within the districts. These units help deliver services across vast distances in a country where much of the landscape is desert or wildlife reserves. They coordinate everything from cattle farming to tourism management in Africa's oldest continuous democracy.",
    path: "/geojson/regions/adm/BWA_ADM2.json",
    geography: "Botswana",
    topics: ["Administrative Regions","Botswana","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 25,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "gha-adm1",
    title: "Ghana · Regions (1st level)",
    description: "Ghana's 16 regions stretch from Savannah grasslands in the north to Atlantic beaches and rainforests in the south. These regions follow both colonial boundaries and traditional kingdoms like Ashanti. From bustling Greater Accra to rural Upper East, each region reflects Ghana's ethnic diversity and position as West Africa's democratic success story.",
    path: "/geojson/regions/adm/GHA_ADM1.json",
    geography: "Ghana",
    topics: ["Administrative Regions","Ghana","West Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 16,
    propertyKeys: ["Name"],
  },

  {
    id: "gha-adm2",
    title: "Ghana · Districts (2nd level)",
    description: "Ghana's 260 districts form the primary units of local government, created through decades of gradual decentralization. These districts manage everything from cocoa farming to gold mining to coastal fishing. They're essential for delivering services in a country that pioneered African independence in 1957.",
    path: "/geojson/regions/adm/GHA_ADM2.json",
    geography: "Ghana",
    topics: ["Administrative Regions","Ghana","West Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 260,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "gum-adm2",
    title: "Guam · Villages (2nd level)",
    description: "Guam's 19 villages organize this Pacific island territory that's been under US control since 1898. These villages blend Chamorro indigenous culture with American administration. From Hagåtña (the capital) to Yona in the south, they manage services on this strategic military outpost and tropical tourism destination.",
    path: "/geojson/regions/adm/GUM_ADM2.json",
    geography: "Guam",
    topics: ["Administrative Regions","Guam","Oceania"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 19,
    propertyKeys: ["Name"],
  },

  {
    id: "isr-adm1",
    title: "Israel · Districts (1st level)",
    description: "Israel's 6 districts organize governance from Mediterranean coast to Negev desert to occupied territories. These mehozot coordinate services across a small but diverse country with dramatic regional differences. From Tel Aviv's metropolitan bustle to Jerusalem's complex religious significance to Beersheba's desert gateway, each district reflects Israel's complexities.",
    path: "/geojson/regions/adm/ISR_ADM1.json",
    geography: "Israel",
    topics: ["Administrative Regions","Israel","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 6,
    propertyKeys: ["Name"],
  },

  {
    id: "isr-adm2",
    title: "Israel · Sub-districts (2nd level)",
    description: "Israel's 15 sub-districts provide more localized administration within districts. These subdivisions help coordinate services from kibbutzim to Arab towns to ultra-Orthodox communities. They manage governance across Israel's diverse population and contested territories, including areas of international dispute.",
    path: "/geojson/regions/adm/ISR_ADM2.json",
    geography: "Israel",
    topics: ["Administrative Regions","Israel","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 15,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "jor-adm1",
    title: "Jordan · Governorates (1st level)",
    description: "Jordan's 12 governorates organize the Hashemite Kingdom from the Dead Sea (Earth's lowest point) to desert plateaus. These muhafazat balance Bedouin heritage with modern governance, hosting huge refugee populations from regional conflicts. From Amman's urban sprawl to Petra's ancient ruins to Aqaba's Red Sea coast, they coordinate services across limited water resources and regional challenges.",
    path: "/geojson/regions/adm/JOR_ADM1.json",
    geography: "Jordan",
    topics: ["Administrative Regions","Jordan","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 12,
    propertyKeys: ["Name"],
  },

  {
    id: "jor-adm2",
    title: "Jordan · Districts (2nd level)",
    description: "Jordan's 52 districts subdivide governorates across desert kingdoms and fertile highlands. These liwa coordinate local administration in a country balancing regional instability with internal stability. They manage services from refugee camps to agricultural valleys to tourism sites like Jerash's Roman ruins.",
    path: "/geojson/regions/adm/JOR_ADM2.json",
    geography: "Jordan",
    topics: ["Administrative Regions","Jordan","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 52,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "ken-adm1",
    title: "Kenya · Counties (1st level)",
    description: "Kenya's 47 counties established by the 2010 constitution replaced the old provincial system, giving unprecedented autonomy to regions. These counties span from Mount Kenya's highlands to Turkana's arid north to Mombasa's Indian Ocean coast. Each county has an elected governor and manages services including healthcare and agriculture across Kenya's diverse landscapes.",
    path: "/geojson/regions/adm/KEN_ADM1.json",
    geography: "Kenya",
    topics: ["Administrative Regions","Kenya","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 47,
    propertyKeys: ["Name"],
  },

  {
    id: "ken-adm2",
    title: "Kenya · Sub-counties (2nd level)",
    description: "Kenya's 290 sub-counties subdivide the counties for more localized administration. These units help coordinate services from the Great Rift Valley to coastal regions. They're crucial for managing Kenya's ethnic diversity and rapid urbanization around Nairobi and other major cities.",
    path: "/geojson/regions/adm/KEN_ADM2.json",
    geography: "Kenya",
    topics: ["Administrative Regions","Kenya","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 290,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "ken-adm3",
    title: "Kenya · Wards (3rd level)",
    description: "Kenya's 1,450 wards represent the smallest electoral and administrative units, each electing a member to county assemblies. These wards deliver grassroots services from tea plantations in the highlands to pastoralist communities in the north. They're essential for democratic participation in East Africa's largest economy.",
    path: "/geojson/regions/adm/KEN_ADM3.json",
    geography: "Kenya",
    topics: ["Administrative Regions","Kenya","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 1452,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "lso-adm1",
    title: "Lesotho · Districts (1st level)",
    description: "Lesotho's 10 districts organize the 'Kingdom in the Sky', entirely surrounded by South Africa and lying above 1,400 meters elevation. These districts follow mountain ranges and river valleys in southern Africa's only independent country that's entirely above 1,000 meters. From lowlands Maseru to highland Mokhotlong, they manage services across challenging alpine terrain.",
    path: "/geojson/regions/adm/LSO_ADM1.json",
    geography: "Lesotho",
    topics: ["Administrative Regions","Lesotho","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 10,
    propertyKeys: ["Name"],
  },

  {
    id: "lso-adm2",
    title: "Lesotho · Constituencies (2nd level)",
    description: "Lesotho's 78 constituencies serve as electoral and administrative divisions within districts. These units help coordinate governance across mountainous terrain where many communities remain isolated during winter. They balance traditional chieftaincy with elected councils in Africa's last remaining mountain kingdom.",
    path: "/geojson/regions/adm/LSO_ADM2.json",
    geography: "Lesotho",
    topics: ["Administrative Regions","Lesotho","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 78,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "mdg-adm1",
    title: "Madagascar · Regions (1st level)",
    description: "Madagascar's 22 regions organize the world's fourth-largest island, home to wildlife found nowhere else on Earth. These regions were created in 2007 reforms, replacing six larger provinces. From coastal Menabe with its baobabs to highland Antananarivo to rainforest Atsinanana, each region showcases Madagascar's unique biodiversity and Malagasy culture.",
    path: "/geojson/regions/adm/MDG_ADM1.json",
    geography: "Madagascar",
    topics: ["Administrative Regions","Madagascar","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 22,
    propertyKeys: ["Name"],
  },

  {
    id: "mdg-adm2",
    title: "Madagascar · Districts (2nd level)",
    description: "Madagascar's 119 districts subdivide the regions across diverse landscapes from tropical beaches to central highlands. These districts coordinate services in a biodiversity hotspot where 90% of wildlife is endemic. They manage challenges from vanilla cultivation to rainforest conservation across this isolated island nation.",
    path: "/geojson/regions/adm/MDG_ADM2.json",
    geography: "Madagascar",
    topics: ["Administrative Regions","Madagascar","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 119,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "mdg-adm3",
    title: "Madagascar · Communes (3rd level)",
    description: "Madagascar's 1,579 communes form the foundation of local government across the island. These communes range from rice-farming rural areas to coastal fishing villages. They're crucial for managing services in a country where infrastructure remains limited and many communities are accessible only by foot or boat.",
    path: "/geojson/regions/adm/MDG_ADM3.json",
    geography: "Madagascar",
    topics: ["Administrative Regions","Madagascar","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 1579,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "mnp-adm2",
    title: "Northern Mariana Islands · Municipalities (2nd level)",
    description: "Northern Mariana Islands' 4 municipalities govern this US Commonwealth scattered across the Pacific. These include Saipan (the capital and largest), Tinian, Rota, and the Northern Islands. The municipalities blend Chamorro and Carolinian cultures with American administration across these remote tropical islands.",
    path: "/geojson/regions/adm/MNP_ADM2.json",
    geography: "Northern Mariana Islands",
    topics: ["Administrative Regions","Northern Mariana Islands","Oceania"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 4,
    propertyKeys: ["Name"],
  },

  {
    id: "nga-adm1",
    title: "Nigeria · States (1st level)",
    description: "Nigeria's 36 states plus the Federal Capital Territory (Abuja) organize Africa's most populous nation of over 200 million people. These states balance Nigeria's three major ethnic groups (Yoruba, Hausa, Igbo) and over 500 languages. From oil-rich Rivers in the Niger Delta to commercial Lagos to Kano in the north, each state reflects Nigeria's remarkable diversity and economic power.",
    path: "/geojson/regions/adm/NGA_ADM1.json",
    geography: "Nigeria",
    topics: ["Administrative Regions","Nigeria","West Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 37,
    propertyKeys: ["Name"],
  },

  {
    id: "nga-adm2",
    title: "Nigeria · Local Government Areas (2nd level)",
    description: "Nigeria's 774 local government areas form the grassroots administrative structure across the federation. These LGAs manage services from the Sahel savanna in the north to tropical rainforests in the south. They coordinate everything from petroleum industry impacts to agriculture to managing Africa's largest city, Lagos.",
    path: "/geojson/regions/adm/NGA_ADM2.json",
    geography: "Nigeria",
    topics: ["Administrative Regions","Nigeria","West Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 774,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "nzl-adm1",
    title: "New Zealand · Regions (1st level)",
    description: "New Zealand's 16 regions plus the Chatham Islands organize governance from North Island's volcanic plateau to South Island's Southern Alps. These regions were established in 1989 reforms, managing environmental protection and regional planning. From subtropical Northland to sub-Antarctic Southland, they coordinate services across dramatic landscapes and a maritime economy.",
    path: "/geojson/regions/adm/NZL_ADM1.json",
    geography: "New Zealand",
    topics: ["Administrative Regions","New Zealand","Oceania"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 17,
    propertyKeys: ["Name"],
  },

  {
    id: "nzl-adm2",
    title: "New Zealand · Territorial Authorities (2nd level)",
    description: "New Zealand's 67 territorial authorities (cities and districts) form the primary units of local government, managing everything from urban Auckland to remote Chatham Islands. These authorities balance environmental protection with development in a country renowned for natural beauty. They coordinate services from dairy farming to adventure tourism across both main islands.",
    path: "/geojson/regions/adm/NZL_ADM2.json",
    geography: "New Zealand",
    topics: ["Administrative Regions","New Zealand","Oceania"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 88,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "nzl-adm3",
    title: "New Zealand · Wards (3rd level)",
    description: "New Zealand's 245 wards subdivide cities and districts for electoral representation and local service delivery. These wards help ensure communities from Māori regions to farming districts to cities have representation. They're essential for New Zealand's consensus-based democracy and bicultural identity.",
    path: "/geojson/regions/adm/NZL_ADM3.json",
    geography: "New Zealand",
    topics: ["Administrative Regions","New Zealand","Oceania"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 245,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "pse-adm1",
    title: "Palestine · Governorates (1st level)",
    description: "Palestine's 2 governorate groups—West Bank and Gaza Strip—reflect the political reality of occupied territories seeking statehood. These areas organized by the Palestinian Authority coordinate services under Israeli occupation. From Ramallah's administrative center to Hebron's old city to Gaza's dense population, they manage governance amid ongoing conflict.",
    path: "/geojson/regions/adm/PSE_ADM1.json",
    geography: "Palestine",
    topics: ["Administrative Regions","Palestine","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 2,
    propertyKeys: ["Name"],
  },

  {
    id: "pse-adm2",
    title: "Palestine · Districts (2nd level)",
    description: "Palestine's 16 governorates provide more localized administration across the West Bank and Gaza Strip. These muhafazat coordinate services despite Israeli checkpoints, settlements, and restrictions. They manage everything from olive farming to refugee camps to preserving cultural heritage in a nation under occupation.",
    path: "/geojson/regions/adm/PSE_ADM2.json",
    geography: "Palestine",
    topics: ["Administrative Regions","Palestine","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 16,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "qat-adm1",
    title: "Qatar · Municipalities (1st level)",
    description: "Qatar's 8 municipalities organize this small but wealthy Gulf state, transformed by natural gas reserves into one of the world's richest countries per capita. These baladiyat range from Doha's futuristic skyline to northern industrial zones to southern desert. The municipality system coordinates rapid development and infrastructure for Doha's 2022 World Cup legacy.",
    path: "/geojson/regions/adm/QAT_ADM1.json",
    geography: "Qatar",
    topics: ["Administrative Regions","Qatar","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 8,
    propertyKeys: ["Name"],
  },

  {
    id: "qat-adm2",
    title: "Qatar · Zones (2nd level)",
    description: "Qatar's 79 zones subdivide municipalities for more detailed planning and service delivery. These zones help manage Qatar's transformation from pearl-diving to petrochemical wealth. They coordinate services for a population that's 90% expatriate, from gleaming towers to labor camps to traditional souqs.",
    path: "/geojson/regions/adm/QAT_ADM2.json",
    geography: "Qatar",
    topics: ["Administrative Regions","Qatar","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 79,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "rwa-adm1",
    title: "Rwanda · Provinces (1st level)",
    description: "Rwanda's 5 provinces organize one of Africa's most densely populated countries, known for dramatic recovery since the 1994 genocide. These provinces range from volcanic mountains in the north to savanna in the east. Rwanda's efficient governance and mountain gorilla conservation have made these provinces a model for African development.",
    path: "/geojson/regions/adm/RWA_ADM1.json",
    geography: "Rwanda",
    topics: ["Administrative Regions","Rwanda","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 5,
    propertyKeys: ["Name"],
  },

  {
    id: "rwa-adm2",
    title: "Rwanda · Districts (2nd level)",
    description: "Rwanda's 30 districts implement the country's ambitious development programs from Kigali's clean streets to rural tea plantations. These districts coordinate services across 'the land of a thousand hills' where most people still farm on steep hillsides. They've been central to Rwanda's transformation into one of Africa's fastest-growing economies.",
    path: "/geojson/regions/adm/RWA_ADM2.json",
    geography: "Rwanda",
    topics: ["Administrative Regions","Rwanda","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 30,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "rwa-adm3",
    title: "Rwanda · Sectors (3rd level)",
    description: "Rwanda's 416 sectors represent the most local administrative level, often encompassing several villages. These sectors deliver healthcare, education, and community policing programs that have dramatically improved living standards. They're essential for implementing Rwanda's monthly community service (Umuganda) and other national initiatives.",
    path: "/geojson/regions/adm/RWA_ADM3.json",
    geography: "Rwanda",
    topics: ["Administrative Regions","Rwanda","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 416,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "sen-adm1",
    title: "Senegal · Regions (1st level)",
    description: "Senegal's 14 regions stretch from Sahel in the north to tropical Casamance in the south. These regions balance Senegal's position as West Africa's westernmost point with historic ties to French colonialism. From peanut-farming interior to coastal Dakar, Africa's gateway to Europe, each region reflects Senegal's stable democracy and rich cultural heritage.",
    path: "/geojson/regions/adm/SEN_ADM1.json",
    geography: "Senegal",
    topics: ["Administrative Regions","Senegal","West Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 14,
    propertyKeys: ["Name"],
  },

  {
    id: "sen-adm2",
    title: "Senegal · Departments (2nd level)",
    description: "Senegal's 45 departments subdivide the regions across varied landscapes from the Sahara fringe to mangrove coastlines. These departments coordinate services in a country known for religious tolerance and political stability. They manage everything from groundnut agriculture to fishing to Dakar's position as a regional hub.",
    path: "/geojson/regions/adm/SEN_ADM2.json",
    geography: "Senegal",
    topics: ["Administrative Regions","Senegal","West Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 45,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "sen-adm3",
    title: "Senegal · Arrondissements (3rd level)",
    description: "Senegal's 121 arrondissements provide more localized administration within departments. These units help deliver services from the historic island of Gorée to rural villages in the Ferlo desert. They're crucial for managing Senegal's predominantly agricultural economy and urbanizing coastal areas.",
    path: "/geojson/regions/adm/SEN_ADM3.json",
    geography: "Senegal",
    topics: ["Administrative Regions","Senegal","West Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 121,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "swz-adm1",
    title: "Eswatini · Regions (1st level)",
    description: "Eswatini's 4 regions organize Africa's last absolute monarchy, a small landlocked kingdom between South Africa and Mozambique. These regions—Hhohho, Manzini, Lubombo, Shiselweni—follow traditional boundaries and manage both modern governance and traditional authority. From highveld to lowveld, they coordinate services across varied terrain in this sugar-producing nation.",
    path: "/geojson/regions/adm/SWZ_ADM1.json",
    geography: "Eswatini",
    topics: ["Administrative Regions","Eswatini","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 4,
    propertyKeys: ["Name"],
  },

  {
    id: "swz-adm2",
    title: "Eswatini · Tinkhundla (2nd level)",
    description: "Eswatini's 53 tinkhundla (administrative subdivisions) blend traditional and modern governance in this kingdom formerly known as Swaziland. Each tinkhundla includes several chiefdoms and serves as both electoral and administrative units. They're unique to Eswatini's political system, which combines monarchy with limited democracy.",
    path: "/geojson/regions/adm/SWZ_ADM2.json",
    geography: "Eswatini",
    topics: ["Administrative Regions","Eswatini","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 53,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "tun-adm1",
    title: "Tunisia · Governorates (1st level)",
    description: "Tunisia's 24 governorates span from Mediterranean beaches to Sahara Desert, organizing the birthplace of the Arab Spring. These wilayat balance ancient heritage (Carthage, Roman sites) with modern democracy. From cosmopolitan Tunis to olive-growing Sfax to desert Tozeur, each governorate reflects Tunisia's position bridging Arab, African, and Mediterranean identities.",
    path: "/geojson/regions/adm/TUN_ADM1.json",
    geography: "Tunisia",
    topics: ["Administrative Regions","Tunisia","North Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 24,
    propertyKeys: ["Name"],
  },

  {
    id: "tun-adm2",
    title: "Tunisia · Delegations (2nd level)",
    description: "Tunisia's 264 delegations provide intermediate administration within governorates. These mu'tamadiyat coordinate services from coastal tourism to interior agriculture to phosphate mining in the south. They've been crucial for managing Tunisia's democratic transition since the 2011 revolution that sparked regional upheaval.",
    path: "/geojson/regions/adm/TUN_ADM2.json",
    geography: "Tunisia",
    topics: ["Administrative Regions","Tunisia","North Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 264,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "tur-adm1",
    title: "Turkey · Provinces (1st level)",
    description: "Turkey's 81 provinces organize a nation bridging Europe and Asia, from Aegean coast to eastern Anatolia. These iller reflect Turkey's geographic and cultural diversity, from secular Istanbul to conservative Konya to Kurdish-majority southeast. Each province balances Ottoman heritage with Atatürk's modernization and contemporary challenges of democracy and development.",
    path: "/geojson/regions/adm/TUR_ADM1.json",
    geography: "Turkey",
    topics: ["Administrative Regions","Turkey","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 81,
    propertyKeys: ["Name"],
  },

  {
    id: "tur-adm2",
    title: "Turkey · Districts (2nd level)",
    description: "Turkey's 973 districts subdivide provinces across varied terrain from Black Sea mountains to Mediterranean beaches. These ilçeler coordinate services in a rapidly developing country of 85 million. They manage everything from tea farming in Rize to tourism in Antalya to earthquake-prone zones across the North Anatolian Fault.",
    path: "/geojson/regions/adm/TUR_ADM2.json",
    geography: "Turkey",
    topics: ["Administrative Regions","Turkey","Middle East"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 973,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "uga-adm1",
    title: "Uganda · Regions (1st level)",
    description: "Uganda's 4 regions—Central, Eastern, Northern, Western—organize the 'Pearl of Africa' from Lake Victoria to the Rwenzori Mountains. These administrative regions coordinate services across diverse terrain including the source of the Nile. They help manage governance in a country recovering from conflict and building its economy on agriculture and emerging oil reserves.",
    path: "/geojson/regions/adm/UGA_ADM1.json",
    geography: "Uganda",
    topics: ["Administrative Regions","Uganda","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 4,
    propertyKeys: ["Name"],
  },

  {
    id: "uga-adm2",
    title: "Uganda · Districts (2nd level)",
    description: "Uganda's 151 districts have proliferated from just 39 at independence to improve service delivery and political representation. These districts range from Kampala's urban sprawl to remote Karamoja in the northeast. They manage challenges from mountain gorilla conservation to coffee farming across Uganda's fertile landscapes.",
    path: "/geojson/regions/adm/UGA_ADM2.json",
    geography: "Uganda",
    topics: ["Administrative Regions","Uganda","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 151,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "uga-adm3",
    title: "Uganda · Sub-counties (3rd level)",
    description: "Uganda's 137 sub-counties form the primary units of local service delivery across districts. These sub-counties often correspond to traditional kingdoms and tribal areas. They're essential for managing healthcare, education, and agriculture extension services in a predominantly rural country.",
    path: "/geojson/regions/adm/UGA_ADM3.json",
    geography: "Uganda",
    topics: ["Administrative Regions","Uganda","East Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 137,
    propertyKeys: ["Name", "District", "Province"],
  },

  {
    id: "zaf-adm1",
    title: "South Africa · Provinces (1st level)",
    description: "South Africa's 9 provinces emerged from the 1994 democratic transition, replacing the apartheid-era structure. From Western Cape's Mediterranean climate to KwaZulu-Natal's Indian Ocean coast to Gauteng's economic heartland, these provinces reflect South Africa's position as Africa's most developed economy. Each province balances the legacy of apartheid with building a unified democracy.",
    path: "/geojson/regions/adm/ZAF_ADM1.json",
    geography: "South Africa",
    topics: ["Administrative Regions","South Africa","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 9,
    propertyKeys: ["Name"],
  },

  {
    id: "zaf-adm2",
    title: "South Africa · District Municipalities (2nd level)",
    description: "South Africa's 52 district municipalities coordinate services across varied landscapes from Kruger National Park to Cape Town's Table Mountain. These districts manage the transition from apartheid's spatial legacy to integrated development. They coordinate everything from mining in Limpopo to wine farming in Western Cape.",
    path: "/geojson/regions/adm/ZAF_ADM2.json",
    geography: "South Africa",
    topics: ["Administrative Regions","South Africa","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 52,
    propertyKeys: ["Name", "Province"],
  },

  {
    id: "zaf-adm3",
    title: "South Africa · Local Municipalities (3rd level)",
    description: "South Africa's 213 local municipalities deliver services from townships to suburbs to rural areas. These municipalities face challenges of addressing apartheid's inequalities while building infrastructure and services. They range from eThekwini (Durban) with 3.7 million people to small rural municipalities in the Eastern Cape.",
    path: "/geojson/regions/adm/ZAF_ADM3.json",
    geography: "South Africa",
    topics: ["Administrative Regions","South Africa","Southern Africa"],
    credits: [{
      name: "geoBoundaries",
      url: "https://www.geoboundaries.org/",
    }],
    featureCount: 213,
    propertyKeys: ["Name", "District", "Province"],
  },

  // ============================================
  // City Datasets
  // ============================================

  {
    id: 'alb-cities',
    title: 'Albania · Cities',
    description: "Albania's 107 major cities anchor communities across 28,748 sq km of Balkan terrain. From coastal Durrës to mountainous Kukës, these urban centers connect Mediterranean ports with highland traditions, serving 2.8 million people navigating post-communist transformation.",
    path: '/geojson/cities/ALB_CITIES.json',
    geography: 'Albania',
    topics: ['Cities', 'Albania'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 107,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'arg-cities',
    title: 'Argentina · Cities',
    description: "Argentina's 200 largest cities organize life across 2.78 million sq km stretching from subtropical north to glacial Patagonia. From Buenos Aires' tango halls to Mendoza's vineyards, these urban hubs coordinate cattle ranching, wine production, and services for 46 million people.",
    path: '/geojson/cities/ARG_CITIES.json',
    geography: 'Argentina',
    topics: ['Cities', 'Argentina'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'aus-cities',
    title: 'Australia · Cities',
    description: "Australia's top 200 cities manage services across 7.69 million sq km from tropical Darwin to temperate Hobart. From Sydney's harbor to Perth's isolation, these urban centers coordinate mining booms, coastal tourism, and services for 26 million people in the driest inhabited continent.",
    path: '/geojson/cities/AUS_CITIES.json',
    geography: 'Australia',
    topics: ['Cities', 'Australia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'aut-cities',
    title: 'Austria · Cities',
    description: "Austria's 200 cities nestle across 83,879 sq km of Alpine valleys and Danube plains. From Vienna's imperial architecture to Innsbruck's mountain resorts, these urban centers balance classical music heritage with modern industries serving 9 million people in the heart of Europe.",
    path: '/geojson/cities/AUT_CITIES.json',
    geography: 'Austria',
    topics: ['Cities', 'Austria'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'bel-cities',
    title: 'Belgium · Cities',
    description: "Belgium's top 200 cities pack into 30,528 sq km between North Sea coast and Ardennes forests. From Brussels' EU institutions to Antwerp's diamond trade, these bilingual urban centers coordinate medieval heritage with modern commerce for 11.6 million residents.",
    path: '/geojson/cities/BEL_CITIES.json',
    geography: 'Belgium',
    topics: ['Cities', 'Belgium'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'bgd-cities',
    title: 'Bangladesh · Cities',
    description: "Bangladesh's 200 major cities navigate life across 148,460 sq km of delta lowlands and monsoon rivers. From Dhaka's bustling streets to Chittagong's port facilities, these dense urban centers manage textile industries, river trade, and services for 171 million people.",
    path: '/geojson/cities/BGD_CITIES.json',
    geography: 'Bangladesh',
    topics: ['Cities', 'Bangladesh'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'bgr-cities',
    title: 'Bulgaria · Cities',
    description: "Bulgaria's 200 cities spread across 110,994 sq km from Black Sea beaches to Balkan mountain ranges. From Sofia's Orthodox cathedrals to Plovdiv's Roman ruins, these urban centers balance rose oil production with tech industries serving 6.9 million people.",
    path: '/geojson/cities/BGR_CITIES.json',
    geography: 'Bulgaria',
    topics: ['Cities', 'Bulgaria'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'bol-cities',
    title: 'Bolivia · Cities',
    description: "Bolivia's top 200 cities scale from Amazonian lowlands to Andean highlands across 1.1 million sq km. From La Paz's aerial cable cars to Santa Cruz's tropical sprawl, these urban centers coordinate indigenous traditions, lithium mining, and services for 12 million people.",
    path: '/geojson/cities/BOL_CITIES.json',
    geography: 'Bolivia',
    topics: ['Cities', 'Bolivia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 124,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'bra-cities',
    title: 'Brazil · Cities',
    description: "Brazil's 200 largest cities command 8.5 million sq km from Amazon rainforest to Atlantic beaches. From São Paulo's industrial might to Salvador's Afro-Brazilian culture, these urban powerhouses drive coffee exports, tech innovation, and services for 215 million people.",
    path: '/geojson/cities/BRA_CITIES.json',
    geography: 'Brazil',
    topics: ['Cities', 'Brazil'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'btn-cities',
    title: 'Bhutan · Cities',
    description: "Bhutan's top 200 towns dot 38,394 sq km of Himalayan valleys and Buddhist monasteries. From Thimphu's dzongs to Paro's sacred sites, these small urban centers balance Gross National Happiness philosophy with careful modernization serving 780,000 people.",
    path: '/geojson/cities/BTN_CITIES.json',
    geography: 'Bhutan',
    topics: ['Cities', 'Bhutan'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 18,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'can-cities',
    title: 'Canada · Cities',
    description: "Canada's top 200 cities stretch across 9.98 million sq km from Pacific to Atlantic to Arctic. From Toronto's financial district to Vancouver's Pacific trade, these urban centers manage resource extraction, immigrant integration, and services for 39 million people.",
    path: '/geojson/cities/CAN_CITIES.json',
    geography: 'Canada',
    topics: ['Cities', 'Canada'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'che-cities',
    title: 'Switzerland · Cities',
    description: "Switzerland's 200 cities nestle across 41,290 sq km of Alpine peaks and pristine lakes. From Zürich's banking prowess to Geneva's diplomacy, these multilingual urban centers coordinate precision manufacturing, chocolate exports, and services for 8.7 million residents.",
    path: '/geojson/cities/CHE_CITIES.json',
    geography: 'Switzerland',
    topics: ['Cities', 'Switzerland'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'chl-cities',
    title: 'Chile · Cities',
    description: "Chile's 200 major cities stretch across 756,102 sq km from Atacama Desert to Patagonian fjords. From Santiago's smog-filled basin to Valparaíso's colorful hills, these urban centers coordinate copper mining, wine exports, and services for 19 million people.",
    path: '/geojson/cities/CHL_CITIES.json',
    geography: 'Chile',
    topics: ['Cities', 'Chile'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 154,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'chn-cities',
    title: 'China · Cities',
    description: "China's top 200 cities anchor civilization across 9.6 million sq km from Tibetan plateau to Pacific coast. From Beijing's political power to Shenzhen's tech factories, these massive urban centers drive manufacturing exports, high-speed rail, and services for 1.4 billion people.",
    path: '/geojson/cities/CHN_CITIES.json',
    geography: 'China',
    topics: ['Cities', 'China'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'col-cities',
    title: 'Colombia · Cities',
    description: "Colombia's 200 largest cities navigate 1.14 million sq km from Caribbean beaches to Andean coffee zones. From Bogotá's mountain perch to Cartagena's colonial walls, these urban centers balance emerald mining, flower exports, and services for 51 million people.",
    path: '/geojson/cities/COL_CITIES.json',
    geography: 'Colombia',
    topics: ['Cities', 'Colombia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'cri-cities',
    title: 'Costa Rica · Cities',
    description: "Costa Rica's top 200 towns organize across 51,100 sq km of rainforests and volcanic ranges. From San José's central valley to Limón's Caribbean ports, these eco-conscious urban centers coordinate coffee cultivation, tourism services, and conservation for 5.1 million people.",
    path: '/geojson/cities/CRI_CITIES.json',
    geography: 'Costa Rica',
    topics: ['Cities', 'Costa Rica'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 119,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'cze-cities',
    title: 'Czech Republic · Cities',
    description: "Czechia's 200 cities fill 78,866 sq km of Bohemian valleys and Moravian plains. From Prague's Gothic spires to Brno's tech sector, these urban centers balance beer brewing traditions with automotive manufacturing serving 10.5 million people in Central Europe.",
    path: '/geojson/cities/CZE_CITIES.json',
    geography: 'Czech Republic',
    topics: ['Cities', 'Czech Republic'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'deu-cities',
    title: 'Germany · Cities',
    description: "Germany's 200 major cities organize 357,022 sq km from North Sea ports to Alpine foothills. From Berlin's reunified capital to Munich's Oktoberfest, these industrial urban centers coordinate automotive exports, precision engineering, and services for 84 million people.",
    path: '/geojson/cities/DEU_CITIES.json',
    geography: 'Germany',
    topics: ['Cities', 'Germany'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'dnk-cities',
    title: 'Denmark · Cities',
    description: "Denmark's top 200 cities occupy 42,933 sq km of Jutland peninsula and island archipelagos. From Copenhagen's cycling culture to Aarhus's Viking heritage, these compact urban centers balance renewable energy innovation with dairy exports serving 5.9 million people.",
    path: '/geojson/cities/DNK_CITIES.json',
    geography: 'Denmark',
    topics: ['Cities', 'Denmark'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'dom-cities',
    title: 'Dominican Republic · Cities',
    description: "The Dominican Republic's 200 cities span 48,671 sq km of Caribbean beaches and mountain ranges. From Santo Domingo's colonial zone to Puerto Plata's tourism resorts, these urban centers coordinate sugar production, baseball excellence, and services for 11 million people.",
    path: '/geojson/cities/DOM_CITIES.json',
    geography: 'Dominican Republic',
    topics: ['Cities', 'Dominican Republic'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'ecu-cities',
    title: 'Ecuador · Cities',
    description: "Ecuador's top 200 cities navigate 283,561 sq km from Pacific coast through Andes to Amazon basin. From Quito's equatorial altitude to Guayaquil's bustling port, these diverse urban centers coordinate banana exports, oil revenues, and services for 18 million people.",
    path: '/geojson/cities/ECU_CITIES.json',
    geography: 'Ecuador',
    topics: ['Cities', 'Ecuador'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'esp-cities',
    title: 'Spain · Cities',
    description: "Spain's 200 largest cities spread across 505,990 sq km from Atlantic coast to Mediterranean shores. From Madrid's political center to Barcelona's Catalan culture, these historic urban centers balance tourism industries, agriculture, and services for 47 million people.",
    path: '/geojson/cities/ESP_CITIES.json',
    geography: 'Spain',
    topics: ['Cities', 'Spain'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'est-cities',
    title: 'Estonia · Cities',
    description: "Estonia's 200 settlements dot 45,227 sq km of Baltic coastline and northern forests. From Tallinn's digital governance to Tartu's university traditions, these tech-savvy urban centers coordinate e-residency programs, timber exports, and services for 1.3 million people.",
    path: '/geojson/cities/EST_CITIES.json',
    geography: 'Estonia',
    topics: ['Cities', 'Estonia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 106,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'fin-cities',
    title: 'Finland · Cities',
    description: "Finland's top 200 cities scatter across 338,424 sq km of lakes, forests, and Arctic wilderness. From Helsinki's design district to Rovaniemi's Arctic Circle, these Nordic urban centers balance Nokia innovation with paper industries serving 5.5 million people.",
    path: '/geojson/cities/FIN_CITIES.json',
    geography: 'Finland',
    topics: ['Cities', 'Finland'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'fra-cities',
    title: 'France · Cities',
    description: "France's 200 major cities organize 643,801 sq km from English Channel to Mediterranean Riviera. From Paris's cultural magnetism to Lyon's gastronomy, these elegant urban centers coordinate wine regions, aerospace industries, and services for 68 million people.",
    path: '/geojson/cities/FRA_CITIES.json',
    geography: 'France',
    topics: ['Cities', 'France'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'gha-cities',
    title: 'Ghana · Cities',
    description: "Ghana's top 200 cities anchor West African life across 238,533 sq km from Atlantic coast to savanna interior. From Accra's bustling markets to Kumasi's Ashanti heritage, these urban centers coordinate cocoa exports, gold mining, and services for 33 million people.",
    path: '/geojson/cities/GHA_CITIES.json',
    geography: 'Ghana',
    topics: ['Cities', 'Ghana'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'grc-cities',
    title: 'Greece · Cities',
    description: "Greece's 200 cities spread across 131,957 sq km of mainland and island archipelagos. From Athens' ancient ruins to Thessaloniki's Byzantine churches, these historic urban centers balance tourism revenues with shipping industries serving 10.6 million people.",
    path: '/geojson/cities/GRC_CITIES.json',
    geography: 'Greece',
    topics: ['Cities', 'Greece'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'gtm-cities',
    title: 'Guatemala · Cities',
    description: "Guatemala's top 200 cities organize 108,889 sq km from Pacific volcanoes to Caribbean lowlands. From Guatemala City's highland capital to Antigua's colonial architecture, these urban centers balance Mayan heritage with coffee exports serving 18 million people.",
    path: '/geojson/cities/GTM_CITIES.json',
    geography: 'Guatemala',
    topics: ['Cities', 'Guatemala'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'gum-cities',
    title: 'Guam · Cities',
    description: "Guam's cities concentrate across just 544 sq km of tropical Pacific island. From Hagåtña's Spanish heritage to Tumon's resort beaches, these strategic urban centers coordinate US military bases, tourism services, and Chamorro culture for 170,000 people.",
    path: '/geojson/cities/GUM_CITIES.json',
    geography: 'Guam',
    topics: ['Cities', 'Guam'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 21,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'hrv-cities',
    title: 'Croatia · Cities',
    description: "Croatia's 200 cities stretch across 56,594 sq km from Adriatic islands to Pannonian plains. From Zagreb's Habsburg architecture to Dubrovnik's marble streets, these historic urban centers balance tourism booms with shipbuilding industries serving 4 million people.",
    path: '/geojson/cities/HRV_CITIES.json',
    geography: 'Croatia',
    topics: ['Cities', 'Croatia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'hun-cities',
    title: 'Hungary · Cities',
    description: "Hungary's top 200 cities occupy 93,030 sq km of Pannonian Basin and Danube riverbanks. From Budapest's thermal baths to Debrecen's reformed churches, these Central European urban centers coordinate automotive plants, agriculture, and services for 9.7 million people.",
    path: '/geojson/cities/HUN_CITIES.json',
    geography: 'Hungary',
    topics: ['Cities', 'Hungary'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'idn-cities',
    title: 'Indonesia · Cities',
    description: "Indonesia's 200 largest cities navigate 1.9 million sq km across 17,000 volcanic islands. From Jakarta's megacity chaos to Bali's tourist havens, these diverse urban centers coordinate palm oil exports, batik traditions, and services for 277 million people.",
    path: '/geojson/cities/IDN_CITIES.json',
    geography: 'Indonesia',
    topics: ['Cities', 'Indonesia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'ind-cities',
    title: 'India · Cities',
    description: "India's top 200 cities anchor civilization across 3.29 million sq km from Himalayas to tropical coasts. From Mumbai's Bollywood studios to Bangalore's tech campuses, these bustling urban centers drive IT services, spice exports, and governance for 1.4 billion people.",
    path: '/geojson/cities/IND_CITIES.json',
    geography: 'India',
    topics: ['Cities', 'India'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'irl-cities',
    title: 'Ireland · Cities',
    description: "Ireland's 200 towns spread across 70,273 sq km of green pastures and Atlantic coastline. From Dublin's literary heritage to Cork's rebel spirit, these friendly urban centers balance tech giants, whiskey distilleries, and services for 5.1 million people.",
    path: '/geojson/cities/IRL_CITIES.json',
    geography: 'Ireland',
    topics: ['Cities', 'Ireland'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'isl-cities',
    title: 'Iceland · Cities',
    description: "Iceland's top 200 settlements dot 103,000 sq km of volcanic landscapes and geothermal fields. From Reykjavik's rainbow streets to Akureyri's northern charm, these isolated urban centers harness renewable energy, fishing industries, and tourism for 370,000 people.",
    path: '/geojson/cities/ISL_CITIES.json',
    geography: 'Iceland',
    topics: ['Cities', 'Iceland'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 35,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'isr-cities',
    title: 'Israel · Cities',
    description: "Israel's 200 cities compress into 22,072 sq km from Mediterranean beaches to Negev Desert. From Tel Aviv's startup scene to Jerusalem's holy sites, these innovative urban centers coordinate high-tech exports, diamond cutting, and services for 9.7 million people.",
    path: '/geojson/cities/ISR_CITIES.json',
    geography: 'Israel',
    topics: ['Cities', 'Israel'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'ita-cities',
    title: 'Italy · Cities',
    description: "Italy's 200 major cities fill 301,340 sq km from Alpine north to Mediterranean islands. From Rome's eternal monuments to Milan's fashion weeks, these cultural urban centers balance tourism revenues, automotive design, and services for 59 million people.",
    path: '/geojson/cities/ITA_CITIES.json',
    geography: 'Italy',
    topics: ['Cities', 'Italy'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'jor-cities',
    title: 'Jordan · Cities',
    description: "Jordan's top 200 cities organize across 89,342 sq km from Dead Sea shores to desert plateaus. From Amman's refugee integration to Petra's rose-red ruins, these resilient urban centers balance phosphate mining, tourism services, and stability for 11 million people.",
    path: '/geojson/cities/JOR_CITIES.json',
    geography: 'Jordan',
    topics: ['Cities', 'Jordan'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 74,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'jpn-cities',
    title: 'Japan · Cities',
    description: "Japan's 200 largest cities pack into 377,975 sq km of volcanic archipelago from Hokkaido to Kyushu. From Tokyo's neon density to Kyoto's temple gardens, these efficient urban centers drive automotive exports, robotics innovation, and services for 125 million people.",
    path: '/geojson/cities/JPN_CITIES.json',
    geography: 'Japan',
    topics: ['Cities', 'Japan'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'ken-cities',
    title: 'Kenya · Cities',
    description: "Kenya's top 200 cities spread across 580,367 sq km from Indian Ocean to Great Rift Valley. From Nairobi's tech innovation to Mombasa's Swahili coast, these dynamic urban centers coordinate safari tourism, tea exports, and services for 54 million people.",
    path: '/geojson/cities/KEN_CITIES.json',
    geography: 'Kenya',
    topics: ['Cities', 'Kenya'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'khm-cities',
    title: 'Cambodia · Cities',
    description: "Cambodia's top 200 cities organize across 181,035 sq km from Angkor temples to Mekong floodplains. From Phnom Penh's royal palaces to Siem Reap's tourist boom, these recovering urban centers coordinate garment factories, rice cultivation, and services for 17 million people.",
    path: '/geojson/cities/KHM_CITIES.json',
    geography: 'Cambodia',
    topics: ['Cities', 'Cambodia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'kor-cities',
    title: 'South Korea · Cities',
    description: 'Top 200 largest cities by population',
    path: '/geojson/cities/KOR_CITIES.json',
    geography: 'South Korea',
    topics: ['Cities', 'South Korea'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'lao-cities',
    title: 'Laos · Cities',
    description: 'Top 117 largest cities by population',
    path: '/geojson/cities/LAO_CITIES.json',
    geography: 'Laos',
    topics: ['Cities', 'Laos'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 117,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'lka-cities',
    title: 'Sri Lanka · Cities',
    description: "Sri Lanka's 200 cities dot 65,610 sq km of tropical island from mountain tea estates to coastal beaches. From Colombo's busy harbor to Kandy's sacred tooth, these diverse urban centers balance tourism revenues, gem mining, and services for 22 million people.",
    path: '/geojson/cities/LKA_CITIES.json',
    geography: 'Sri Lanka',
    topics: ['Cities', 'Sri Lanka'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'ltu-cities',
    title: 'Lithuania · Cities',
    description: "Lithuania's 200 cities spread across 65,300 sq km of Baltic plains and lake districts. From Vilnius's baroque churches to Klaipėda's port facilities, these Baltic urban centers balance laser manufacturing, amber crafts, and services for 2.8 million people.",
    path: '/geojson/cities/LTU_CITIES.json',
    geography: 'Lithuania',
    topics: ['Cities', 'Lithuania'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 123,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'lux-cities',
    title: 'Luxembourg · Cities',
    description: "Luxembourg's top 200 settlements concentrate in just 2,586 sq km between Belgium, France, and Germany. From Luxembourg City's EU institutions to Esch-sur-Alzette's steel heritage, these prosperous urban centers drive banking services, multilingual culture, and governance for 640,000 people.",
    path: '/geojson/cities/LUX_CITIES.json',
    geography: 'Luxembourg',
    topics: ['Cities', 'Luxembourg'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 26,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'lva-cities',
    title: 'Latvia · Cities',
    description: "Latvia's 200 cities occupy 64,589 sq km of Baltic coastline and dense forests. From Riga's Art Nouveau splendor to Daugavpils's Russian heritage, these Baltic urban centers coordinate timber exports, IT services, and traditional song festivals for 1.9 million people.",
    path: '/geojson/cities/LVA_CITIES.json',
    geography: 'Latvia',
    topics: ['Cities', 'Latvia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 108,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'mex-cities',
    title: 'Mexico · Cities',
    description: "Mexico's 200 largest cities command 1.96 million sq km from Pacific beaches to Caribbean coasts. From Mexico City's ancient Aztec foundations to Monterrey's industrial might, these vibrant urban centers drive automotive manufacturing, tequila exports, and services for 128 million people.",
    path: '/geojson/cities/MEX_CITIES.json',
    geography: 'Mexico',
    topics: ['Cities', 'Mexico'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'mlt-cities',
    title: 'Malta · Cities',
    description: "Malta's cities cluster across just 316 sq km of Mediterranean archipelago between Sicily and Africa. From Valletta's fortress walls to Sliema's waterfront, these dense urban centers balance tourism industries, financial services, and maritime heritage for 520,000 people.",
    path: '/geojson/cities/MLT_CITIES.json',
    geography: 'Malta',
    topics: ['Cities', 'Malta'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 31,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'mne-cities',
    title: 'Montenegro · Cities',
    description: "Montenegro's top 200 towns nestle across 13,812 sq km from Adriatic fjords to mountain peaks. From Podgorica's modern capital to Kotor's medieval walls, these Balkan urban centers coordinate tourism development, aluminum production, and services for 620,000 people.",
    path: '/geojson/cities/MNE_CITIES.json',
    geography: 'Montenegro',
    topics: ['Cities', 'Montenegro'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 38,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'mng-cities',
    title: 'Mongolia · Cities',
    description: "Mongolia's 200 cities dot 1.56 million sq km of vast steppes and Gobi Desert. From Ulaanbaatar's ger districts to remote mining towns, these windswept urban centers coordinate nomadic traditions, copper exports, and services for 3.3 million people.",
    path: '/geojson/cities/MNG_CITIES.json',
    geography: 'Mongolia',
    topics: ['Cities', 'Mongolia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 49,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'mnp-cities',
    title: 'Northern Mariana Islands · Cities',
    description: "The Northern Mariana Islands' settlements spread across 464 sq km of Pacific island chain. From Saipan's WWII history to Tinian's casinos, these US territory urban centers balance tourism services, garment factories, and Chamorro culture for 58,000 people.",
    path: '/geojson/cities/MNP_CITIES.json',
    geography: 'Northern Mariana Islands',
    topics: ['Cities', 'Northern Mariana Islands'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 2,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'mys-cities',
    title: 'Malaysia · Cities',
    description: "Malaysia's top 200 cities navigate 330,803 sq km across Malay Peninsula and Borneo rainforests. From Kuala Lumpur's Petronas Towers to Penang's street food, these multicultural urban centers coordinate palm oil dominance, electronics manufacturing, and services for 34 million people.",
    path: '/geojson/cities/MYS_CITIES.json',
    geography: 'Malaysia',
    topics: ['Cities', 'Malaysia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'nga-cities',
    title: 'Nigeria · Cities',
    description: "Nigeria's 200 major cities anchor West Africa's most populous nation across 923,768 sq km from Sahel to Atlantic. From Lagos's chaotic megacity to Abuja's planned capital, these dynamic urban centers coordinate oil revenues, Nollywood productions, and services for 223 million people.",
    path: '/geojson/cities/NGA_CITIES.json',
    geography: 'Nigeria',
    topics: ['Cities', 'Nigeria'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'nld-cities',
    title: 'Netherlands · Cities',
    description: "The Netherlands' top 200 cities organize across 41,543 sq km of reclaimed land below sea level. From Amsterdam's canal rings to Rotterdam's massive port, these engineered urban centers balance flower exports, cycling culture, and services for 17.5 million people.",
    path: '/geojson/cities/NLD_CITIES.json',
    geography: 'Netherlands',
    topics: ['Cities', 'Netherlands'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'nor-cities',
    title: 'Norway · Cities',
    description: "Norway's 200 cities scatter across 385,207 sq km from Arctic north to southern fjords. From Oslo's Viking museums to Bergen's wooden wharves, these oil-wealthy urban centers coordinate sovereign wealth, renewable energy, and services for 5.5 million people.",
    path: '/geojson/cities/NOR_CITIES.json',
    geography: 'Norway',
    topics: ['Cities', 'Norway'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'nzl-cities',
    title: 'New Zealand · Cities',
    description: "New Zealand's top 200 cities spread across 268,021 sq km of volcanic North Island and alpine South Island. From Auckland's sailing harbors to Christchurch's earthquake recovery, these Pacific urban centers balance sheep farming, film tourism, and services for 5.1 million people.",
    path: '/geojson/cities/NZL_CITIES.json',
    geography: 'New Zealand',
    topics: ['Cities', 'New Zealand'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 102,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'pak-cities',
    title: 'Pakistan · Cities',
    description: "Pakistan's 200 largest cities organize 881,913 sq km from Karakoram peaks to Arabian Sea. From Karachi's port megacity to Lahore's Mughal gardens, these dense urban centers coordinate textile exports, cricket passion, and services for 231 million people.",
    path: '/geojson/cities/PAK_CITIES.json',
    geography: 'Pakistan',
    topics: ['Cities', 'Pakistan'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'pan-cities',
    title: 'Panama · Cities',
    description: "Panama's top 200 cities control 75,417 sq km connecting Pacific and Atlantic oceans. From Panama City's canal operations to Colón's free trade zones, these strategic urban centers coordinate shipping revenues, rainforest conservation, and services for 4.4 million people.",
    path: '/geojson/cities/PAN_CITIES.json',
    geography: 'Panama',
    topics: ['Cities', 'Panama'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'per-cities',
    title: 'Peru · Cities',
    description: "Peru's 200 major cities scale 1.29 million sq km from Pacific deserts to Amazon rainforest. From Lima's coastal fog to Cusco's Inca ruins, these diverse urban centers balance copper mining, tourism industries, and services for 34 million people.",
    path: '/geojson/cities/PER_CITIES.json',
    geography: 'Peru',
    topics: ['Cities', 'Peru'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'phl-cities',
    title: 'Philippines · Cities',
    description: "The Philippines' top 200 cities navigate 300,000 sq km across 7,600 tropical islands. From Manila's dense sprawl to Cebu's diving resorts, these archipelago urban centers coordinate remittance economies, call centers, and services for 115 million people.",
    path: '/geojson/cities/PHL_CITIES.json',
    geography: 'Philippines',
    topics: ['Cities', 'Philippines'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'pol-cities',
    title: 'Poland · Cities',
    description: "Poland's 200 cities occupy 312,696 sq km of Central European plains and mountain borders. From Warsaw's Soviet reconstruction to Kraków's medieval market, these resilient urban centers drive automotive assembly, coal mining, and services for 38 million people.",
    path: '/geojson/cities/POL_CITIES.json',
    geography: 'Poland',
    topics: ['Cities', 'Poland'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'pri-cities',
    title: 'Puerto Rico · Cities',
    description: "Puerto Rico's cities pack into 8,870 sq km of Caribbean island between Atlantic and Caribbean. From San Juan's colonial forts to Ponce's coffee heritage, these US territory urban centers balance pharmaceutical manufacturing, tourism services, and salsa culture for 3.2 million people.",
    path: '/geojson/cities/PRI_CITIES.json',
    geography: 'Puerto Rico',
    topics: ['Cities', 'Puerto Rico'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'prt-cities',
    title: 'Portugal · Cities',
    description: "Portugal's top 200 cities spread across 92,090 sq km from Atlantic coast to Spanish border. From Lisbon's hilly tramways to Porto's port wine cellars, these maritime urban centers coordinate tourism booms, cork exports, and services for 10.3 million people.",
    path: '/geojson/cities/PRT_CITIES.json',
    geography: 'Portugal',
    topics: ['Cities', 'Portugal'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'pse-cities',
    title: 'Palestine · Cities',
    description: 'Top 160 largest cities by population',
    path: '/geojson/cities/PSE_CITIES.json',
    geography: 'Palestine',
    topics: ['Cities', 'Palestine'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 160,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'qat-cities',
    title: 'Qatar · Cities',
    description: "Qatar's 200 settlements concentrate across 11,586 sq km of desert peninsula jutting into Persian Gulf. From Doha's futuristic skyline to industrial Mesaieed, these wealthy urban centers coordinate natural gas exports, World Cup stadiums, and services for 2.9 million people.",
    path: '/geojson/cities/QAT_CITIES.json',
    geography: 'Qatar',
    topics: ['Cities', 'Qatar'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 35,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'rou-cities',
    title: 'Romania · Cities',
    description: "Romania's 200 cities spread across 238,397 sq km from Carpathian mountains to Black Sea delta. From Bucharest's Paris-inspired boulevards to Transylvania's medieval towns, these Eastern European urban centers balance automotive manufacturing, wine production, and services for 19 million people.",
    path: '/geojson/cities/ROU_CITIES.json',
    geography: 'Romania',
    topics: ['Cities', 'Romania'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'rus-cities',
    title: 'Russia · Cities',
    description: 'Top 200 largest cities by population',
    path: '/geojson/cities/RUS_CITIES.json',
    geography: 'Russia',
    topics: ['Cities', 'Russia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'sen-cities',
    title: 'Senegal · Cities',
    description: "Senegal's 200 cities anchor West African coast across 196,722 sq km from Sahel interior to Atlantic beaches. From Dakar's vibrant markets to Saint-Louis's colonial charm, these Francophone urban centers coordinate peanut farming, fishing industries, and services for 17 million people.",
    path: '/geojson/cities/SEN_CITIES.json',
    geography: 'Senegal',
    topics: ['Cities', 'Senegal'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 156,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'sgp-cities',
    title: 'Singapore · Cities',
    description: "Singapore's cities merge into one island city-state across just 728 sq km of strategic Southeast Asian position. From Marina Bay's skyline to Jurong's industrial zones, this efficient urban powerhouse drives shipping dominance, financial services, and innovation for 5.9 million people.",
    path: '/geojson/cities/SGP_CITIES.json',
    geography: 'Singapore',
    topics: ['Cities', 'Singapore'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 64,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'srb-cities',
    title: 'Serbia · Cities',
    description: "Serbia's top 200 cities occupy 77,474 sq km of Balkan crossroads and Danube valleys. From Belgrade's fortress confluence to Novi Sad's fortress festivals, these resilient urban centers balance agricultural exports, IT outsourcing, and services for 6.7 million people.",
    path: '/geojson/cities/SRB_CITIES.json',
    geography: 'Serbia',
    topics: ['Cities', 'Serbia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'svk-cities',
    title: 'Slovakia · Cities',
    description: "Slovakia's 200 cities nestle across 49,035 sq km of Carpathian mountains and Danube lowlands. From Bratislava's Danube castles to Košice's steel heritage, these Central European urban centers coordinate automotive assembly, tourism development, and services for 5.4 million people.",
    path: '/geojson/cities/SVK_CITIES.json',
    geography: 'Slovakia',
    topics: ['Cities', 'Slovakia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 188,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'svn-cities',
    title: 'Slovenia · Cities',
    description: "Slovenia's top 200 towns occupy 20,273 sq km from Alpine peaks to Adriatic coastline. From Ljubljana's dragon bridges to Maribor's wine roads, these compact urban centers balance winter tourism, manufacturing exports, and services for 2.1 million people.",
    path: '/geojson/cities/SVN_CITIES.json',
    geography: 'Slovenia',
    topics: ['Cities', 'Slovenia'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'swe-cities',
    title: 'Sweden · Cities',
    description: "Sweden's 200 cities scatter across 450,295 sq km from Arctic Lapland to southern plains. From Stockholm's archipelago to Gothenburg's Volvo plants, these Nordic urban centers coordinate mining industries, IKEA exports, and services for 10.5 million people.",
    path: '/geojson/cities/SWE_CITIES.json',
    geography: 'Sweden',
    topics: ['Cities', 'Sweden'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'tha-cities',
    title: 'Thailand · Cities',
    description: "Thailand's top 200 cities organize across 513,120 sq km from mountainous north to tropical beaches. From Bangkok's temple chaos to Chiang Mai's night markets, these Southeast Asian urban centers coordinate rice exports, tourism industries, and services for 71 million people.",
    path: '/geojson/cities/THA_CITIES.json',
    geography: 'Thailand',
    topics: ['Cities', 'Thailand'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'tur-cities',
    title: 'Turkey · Cities',
    description: "Turkey's 200 largest cities bridge 783,562 sq km across Europe and Asia from Black Sea to Mediterranean. From Istanbul's Bosphorus strait to Ankara's political center, these historic urban centers coordinate textile exports, tourism industries, and services for 85 million people.",
    path: '/geojson/cities/TUR_CITIES.json',
    geography: 'Turkey',
    topics: ['Cities', 'Turkey'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'twn-cities',
    title: 'Taiwan · Cities',
    description: 'Top 200 largest cities by population',
    path: '/geojson/cities/TWN_CITIES.json',
    geography: 'Taiwan',
    topics: ['Cities', 'Taiwan'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'uga-cities',
    title: 'Uganda · Cities',
    description: "Uganda's top 200 cities organize across 241,038 sq km from Lake Victoria to Rwenzori Mountains. From Kampala's bustling hills to Jinja's Nile source, these East African urban centers coordinate coffee cultivation, refugee support, and services for 48 million people.",
    path: '/geojson/cities/UGA_CITIES.json',
    geography: 'Uganda',
    topics: ['Cities', 'Uganda'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 106,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'ukr-cities',
    title: 'Ukraine · Cities',
    description: "Ukraine's 200 major cities spread across 603,628 sq km of European plains from Carpathians to Black Sea. From Kyiv's golden domes to Lviv's Habsburg heritage, these resilient urban centers coordinate grain exports, IT services, and wartime resistance for 41 million people.",
    path: '/geojson/cities/UKR_CITIES.json',
    geography: 'Ukraine',
    topics: ['Cities', 'Ukraine'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'ury-cities',
    title: 'Uruguay · Cities',
    description: "Uruguay's top 200 cities occupy 176,215 sq km of temperate grasslands between Argentina and Brazil. From Montevideo's beachfront rambla to Punta del Este's resorts, these progressive urban centers coordinate beef exports, renewable energy, and services for 3.4 million people.",
    path: '/geojson/cities/URY_CITIES.json',
    geography: 'Uruguay',
    topics: ['Cities', 'Uruguay'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 129,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'usa-cities',
    title: 'United States · Cities',
    description: "America's 200 largest cities command 9.83 million sq km from Arctic Alaska to subtropical Hawaii. From New York's financial power to Los Angeles's entertainment empire, these diverse urban powerhouses drive technological innovation, agricultural exports, and services for 333 million people.",
    path: '/geojson/cities/USA_CITIES.json',
    geography: 'United States',
    topics: ['Cities', 'United States'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'vnm-cities',
    title: 'Vietnam · Cities',
    description: 'Top 200 largest cities by population',
    path: '/geojson/cities/VNM_CITIES.json',
    geography: 'Vietnam',
    topics: ['Cities', 'Vietnam'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

  {
    id: 'zaf-cities',
    title: 'South Africa · Cities',
    description: "South Africa's top 200 cities organize across 1.22 million sq km from Table Mountain to Kruger Park. From Johannesburg's gold reef to Cape Town's vineyards, these multicultural urban centers coordinate mining wealth, wine exports, and services for 60 million people.",
    path: '/geojson/cities/ZAF_CITIES.json',
    geography: 'South Africa',
    topics: ['Cities', 'South Africa'],
    credits: [{
      name: 'Cellery',
      description: 'Sourced from Huwise'
    }],
    featureCount: 200,
    propertyKeys: ['Name', 'LocalName', 'Population'],
  },

];
